import {
  applyResolvedAssetDefinitionsToDocument,
  type AssetDefinition,
  createAssetContext,
  createPageLoadContext,
  isAssetDefinitionList,
  type PageLoadContext,
  type PageMetadataContext,
  type PageMetadataDefinition,
  type PageRenderMode,
  requirePageRoutePath,
  resolveAssetDefinitions,
  resolvePageLocales,
  resolvePageRenderMode,
  type RouteContext,
  type RouteProfileContext,
} from "../components/page.ts";
import type {
  PageAuthorizationMetadata,
  Principal,
} from "../authorization/index.ts";
import { resolvePageAuthorization } from "../authorization/index.ts";
import {
  type AuthorizationRuntimeOptions,
  evaluatePageAuthorization,
  findMissingAuthorizationPolicies,
  resolveCurrentPrincipal,
  setAuthorizationRuntimeOptions,
} from "../authorization/runtime.ts";
import { ensureMainzCustomElementDefined } from "../components/registry.ts";
import {
  MAINZ_LOCALE_CHANGE_EVENT,
  MAINZ_NAVIGATION_ABORT_EVENT,
  MAINZ_NAVIGATION_ERROR_EVENT,
  MAINZ_NAVIGATION_READY_EVENT,
  MAINZ_NAVIGATION_START_EVENT,
  type MainzLocaleChangeDetail,
  type MainzNavigationAbortDetail,
  type MainzNavigationErrorDetail,
  type MainzNavigationReadyDetail,
  type MainzNavigationStartDetail,
} from "../runtime-events.ts";
import {
  buildRouteMetadata,
  resolveLocaleRedirectPath,
  toLocalePathSegment,
} from "../routing/index.ts";
import { normalizeLocaleTag } from "../i18n/index.ts";
import { installAppI18n } from "../i18n/index.ts";
import type { NavigationMode } from "../routing/types.ts";
import { attachServiceContainer, readServiceContainer } from "../di/context.ts";

declare const __MAINZ_NAVIGATION_MODE__: "spa" | "mpa";
declare const __MAINZ_BASE_PATH__: string;
declare const __MAINZ_DEFAULT_LOCALE__: string | undefined;
declare const __MAINZ_LOCALE_PREFIX__: "except-default" | "always";
declare const __MAINZ_SITE_URL__: string | undefined;
declare const __MAINZ_APP_LOCALES__: readonly string[];
import { withServiceContainer } from "../di/context.ts";
import {
  createServiceContainer,
  type ServiceContainer,
  type ServiceRegistration,
} from "../di/container.ts";
import {
  cleanupManagedAppPortalLayers,
  ensureDefaultAppPortalLayer,
  markMainzAppPortalRoot,
} from "../portal/index.ts";
import {
  cleanupAppCommandRegistry,
  ensureAppCommandRegistry,
} from "../commands/runtime.ts";
import type { MainzCommand } from "../commands/command.ts";

const MAINZ_METADATA_MANAGED_ATTR = "data-mainz-metadata-managed";
const MAINZ_DEFINED_APP_CAPTURE_STACK_KEY: unique symbol = Symbol.for(
  "mainz.definedAppCaptureStack",
) as typeof MAINZ_DEFINED_APP_CAPTURE_STACK_KEY;
const MAINZ_APP_DEFINITION_KIND: unique symbol = Symbol.for(
  "mainz.appDefinitionKind",
) as typeof MAINZ_APP_DEFINITION_KIND;
type InternalDefinedRoutedApp = DefinedRoutedApp & {
  readonly [MAINZ_APP_DEFINITION_KIND]: "routed";
};
type InternalDefinedRootApp = DefinedRootApp & {
  readonly [MAINZ_APP_DEFINITION_KIND]: "root";
};
const DEFINED_APP_CAPTURE_STACK = resolveGlobalDefinedAppCaptureStack();

/**
 * Route params extracted from a matched SPA route pattern.
 */
export type SpaRouteParams = Readonly<Record<string, string>>;
/**
 * Resolves the routed path handled by SPA navigation for a given URL.
 */
export type RoutePathResolver = (
  context: { url: URL; basePath: string },
) => string | null;

/**
 * Context emitted when Mainz applies a new locale during navigation.
 */
export interface NavigationLocaleContext {
  /** Normalized locale applied to the current document and route. */
  locale: string;
  /** Full URL associated with the locale change. */
  url: URL;
  /** Application base path used when resolving localized routes. */
  basePath: string;
}

/**
 * Custom element constructor accepted as a SPA page entry.
 */
export interface SpaPageConstructor extends CustomElementConstructor {
  /** Optional page metadata discovered from decorators on the constructor. */
  page?: {
    /** Page-level document metadata collected from the constructor. */
    metadata?: PageMetadataDefinition;
    /** Page-level authorization metadata collected from the constructor. */
    authorization?: PageAuthorizationMetadata;
  };
  /** Returns the custom-element tag name used to instantiate the page. */
  getTagName(): string;
  /** Constructor name used in diagnostics and route errors. */
  name: string;
}

/**
 * Module shape accepted by lazy SPA route loaders.
 */
export type SpaPageModule = SpaPageConstructor | {
  default: SpaPageConstructor;
};

/**
 * Explicit route metadata for a SPA entry.
 */
export interface SpaRouteDefinition {
  /** Route path resolved by SPA navigation. */
  path: string;
}

/**
 * Eager SPA page registration with an optional explicit path override.
 */
export interface SpaPageDefinition {
  /** Page constructor used for the route. */
  page: SpaPageConstructor;
  /** Explicit route path used instead of reading @Route(...) from the page. */
  path?: string;
}

/**
 * Lazy SPA page registration that defers loading until the route is visited.
 */
export interface SpaLazyPageDefinition extends SpaRouteDefinition {
  /** Loads the page module for the matched route. */
  load(): Promise<SpaPageModule>;
}

export interface SpaNavigationRenderContext {
  page: SpaPageConstructor;
  path: string;
  matchedPath: string;
  params: SpaRouteParams;
  principal: Principal;
  authorization?: PageAuthorizationMetadata;
  data?: unknown;
  metadata?: PageMetadataDefinition;
  assets?: readonly AssetDefinition[];
  locale?: string;
  url: URL;
  navigationType: "initial" | "push" | "pop";
  basePath: string;
}

interface SpaNavigationOptions {
  pages:
    readonly (SpaPageConstructor | SpaPageDefinition | SpaLazyPageDefinition)[];
  notFound?: SpaPageConstructor | SpaPageDefinition | SpaLazyPageDefinition;
  mount?: string | Element;
  auth?: AuthorizationRuntimeOptions;
  services?: readonly ServiceRegistration[];
  locales?: readonly string[];
  resolvePath?: RoutePathResolver;
  onLocaleChange?(context: NavigationLocaleContext): void;
  onRoute?(context: SpaNavigationRenderContext): void;
  onBeforeRender?(context: SpaNavigationRenderContext): void;
}

/**
 * Internal runtime bootstrap used by Mainz internals and focused framework tests.
 *
 * Application startup should prefer defineApp(...) + startApp(...).
 */
export interface InternalStartNavigationOptions {
  appId?: string;
  commands?: readonly MainzCommand<never>[];
  assets?: readonly AssetDefinition[];
  mode: NavigationMode;
  basePath?: string;
  mount?: string | Element;
  pages?:
    readonly (SpaPageConstructor | SpaPageDefinition | SpaLazyPageDefinition)[];
  notFound?: SpaPageConstructor | SpaPageDefinition | SpaLazyPageDefinition;
  auth?: AuthorizationRuntimeOptions;
  services?: readonly ServiceRegistration[];
  locales?: readonly string[];
  resolvePath?: RoutePathResolver;
  onLocaleChange?(context: NavigationLocaleContext): void;
  onRoute?(context: SpaNavigationRenderContext): void;
  onBeforeRender?(context: SpaNavigationRenderContext): void;
  spa?: SpaNavigationOptions;
}

/**
 * Internationalization settings shared by a routed app.
 */
export interface RoutedAppI18nDefinition<Locale extends string = string> {
  /** Supported locales handled by the application. */
  locales: readonly Locale[];
  /** Default locale used when no explicit locale segment is present. */
  defaultLocale: Locale;
  /** Controls whether locale segments are always present in routed URLs. */
  localePrefix?: "always" | "except-default";
  /** Translation dictionaries keyed by locale for framework-owned app i18n. */
  dictionaries?: Record<Locale, object>;
  /** Missing translation behavior for the app-owned runtime. */
  missing?: {
    onKey?: "warn" | "error" | "silent";
    fallback?: "default-locale" | Locale;
  };
}

/**
 * Authorization settings shared by a routed app.
 */
export interface RoutedAppAuthorizationDefinition {
  /** Policy names that must be registered before protected routes can render. */
  policyNames?: readonly string[];
}

/**
 * Application definition for a routed Mainz app.
 */
export interface RoutedAppDefinition {
  /** Stable application id used for runtime markers and diagnostics. */
  id: string;
  /** Authorization settings applied to routed pages. */
  authorization?: RoutedAppAuthorizationDefinition;
  /** Command registrations available to the application shell. */
  commands?: readonly MainzCommand<never>[];
  /** Navigation mode used when starting the app. */
  navigation?: NavigationMode;
  /** Internationalization settings for routed pages. */
  i18n?: RoutedAppI18nDefinition;
  /** Page entries available to the routed application. */
  pages:
    readonly (SpaPageConstructor | SpaPageDefinition | SpaLazyPageDefinition)[];
  /** Optional catch-all page rendered when no route matches. */
  notFound?: SpaPageConstructor | SpaPageDefinition | SpaLazyPageDefinition;
  /** App-wide document assets resolved for routed pages. */
  assets?: readonly AssetDefinition[];
  /** Service registrations attached to the application container. */
  services?: readonly ServiceRegistration[];
}

/**
 * Root component constructor accepted by root-only Mainz apps.
 */
export type AppRootComponentConstructor = CustomElementConstructor & {
  /** Returns the custom-element tag name used to instantiate the root component. */
  getTagName(): string;
  /** Constructor name used in diagnostics and default app ids. */
  name: string;
};

/**
 * Application definition for a root-only Mainz app.
 */
export interface RootAppDefinition {
  /** Stable application id used for runtime markers and diagnostics. */
  id: string;
  /** Command registrations available to the application shell. */
  commands?: readonly MainzCommand<never>[];
  /** Root component rendered directly into the application mount. */
  root: AppRootComponentConstructor;
  /** App-wide document assets resolved for the application shell. */
  assets?: readonly AssetDefinition[];
  /** Service registrations attached to the application container. */
  services?: readonly ServiceRegistration[];
}

/**
 * Routed app definition prepared by {@link defineApp}.
 */
export interface DefinedRoutedApp extends RoutedAppDefinition {}
/**
 * Root app definition prepared by {@link defineApp}.
 */
export interface DefinedRootApp extends RootAppDefinition {}
/**
 * Any Mainz app definition prepared by {@link defineApp}.
 */
export type DefinedApp = DefinedRoutedApp | DefinedRootApp;

/**
 * Runtime options used when starting a defined Mainz app.
 */
export interface StartDefinedAppOptions {
  /** Mount target used for the running application. */
  mount?: string | Element;
  /** Authorization runtime used for page evaluation during startup. */
  auth?: AuthorizationRuntimeOptions;
}

/**
 * Runtime handle returned when a Mainz app is started.
 */
export interface NavigationController {
  /** Navigation mode currently managed by the controller. */
  mode: NavigationMode;
  /** Tears down listeners, portals, commands, and mounted app state. */
  cleanup(): void;
}

type WindowWithMainzCleanupRegistry = Window & {
  __MAINZ_WINDOW_CLEANUPS__?: Set<() => void>;
};

interface NavigationAnchorOptions {
  basePath?: string;
}

interface NormalizedSpaRoute {
  path: string;
  page?: SpaPageConstructor;
  load?: SpaLazyPageDefinition["load"];
}

interface SpaRouteMatch {
  route: NormalizedSpaRoute;
  params: Record<string, string>;
}

interface ResolvedPageNavigationOptions {
  appId?: string;
  commands?: readonly MainzCommand<never>[];
  assets?: readonly AssetDefinition[];
  pages:
    readonly (SpaPageConstructor | SpaPageDefinition | SpaLazyPageDefinition)[];
  notFound?: SpaPageConstructor | SpaPageDefinition | SpaLazyPageDefinition;
  mount?: string | Element;
  auth?: AuthorizationRuntimeOptions;
  services?: readonly ServiceRegistration[];
  serviceContainer?: ServiceContainer;
  locales?: readonly string[];
  resolvePath?: RoutePathResolver;
  onLocaleChange?(context: NavigationLocaleContext): void;
  onRoute?(context: SpaNavigationRenderContext): void;
}

interface InitialRouteSnapshot {
  pageTagName: string;
  path: string;
  matchedPath: string;
  params: Record<string, string>;
  locale?: string;
  data?: unknown;
  metadata?: PageMetadataDefinition;
  assets?: readonly AssetDefinition[];
}

type RoutedPageElement = HTMLElement & {
  props?: Record<string, unknown>;
  rerender?: () => void;
  load?(context: PageLoadContext): unknown | Promise<unknown>;
  metadata(context?: PageMetadataContext): PageMetadataDefinition | undefined;
  assets?(
    context?: PageMetadataContext,
  ): readonly AssetDefinition[] | undefined;
  data?: unknown;
};

type NavigationSequenceSource = () => number;
type NavigationLifecycleBaseArgs = Omit<
  NavigationLifecycleEmissionArgs,
  "sequence"
>;
type NavigationSequenceState = {
  navigationSequence: number;
  started: boolean;
  controller: AbortController;
  lifecycle?: NavigationLifecycleBaseArgs;
  terminalState?: "ready" | "error" | "abort";
};
type NavigationLifecycleEmissionArgs = {
  sequence: NavigationSequenceState;
  mount: HTMLElement;
  mode: NavigationMode;
  navigationType: SpaNavigationRenderContext["navigationType"];
  path: string;
  matchedPath: string;
  locale?: string;
  url: URL;
  basePath: string;
};

class NavigationAbortedError extends Error {
  constructor() {
    super("Navigation aborted.");
    this.name = "NavigationAbortedError";
  }
}

function isAbortLikeError(error: unknown): boolean {
  if (error instanceof NavigationAbortedError) {
    return true;
  }

  if (typeof DOMException !== "undefined" && error instanceof DOMException) {
    return error.name === "AbortError";
  }

  return error instanceof Error && error.name === "AbortError";
}

/**
 * Validates and brands a routed app definition for later startup.
 */
export function defineApp(app: RoutedAppDefinition): DefinedRoutedApp;
/**
 * Validates and brands a root-only app definition for later startup.
 */
export function defineApp(app: RootAppDefinition): DefinedRootApp;
export function defineApp(
  app: RoutedAppDefinition | RootAppDefinition,
): DefinedRoutedApp | DefinedRootApp {
  if (isRoutedAppDefinitionShape(app)) {
    validateRoutedAppDefinition(app);
    const definedApp = brandDefinedApp(
      app,
      "routed",
    ) as InternalDefinedRoutedApp;
    if (definedApp.i18n?.dictionaries) {
      installAppI18n(definedApp.i18n);
    }
    captureDefinedApp(definedApp);
    return definedApp;
  }

  if (isRootAppDefinitionShape(app)) {
    const definedApp = brandDefinedApp(app, "root") as InternalDefinedRootApp;
    captureDefinedApp(definedApp);
    return definedApp;
  }

  return app as DefinedRoutedApp | DefinedRootApp;
}

export async function captureDefinedAppDuring<Value>(
  action: () => Value | Promise<Value>,
): Promise<{ value: Value; app?: DefinedApp }> {
  let capturedApp: DefinedApp | undefined;
  DEFINED_APP_CAPTURE_STACK.push((app) => {
    capturedApp ??= app;
  });

  try {
    const value = await action();
    return {
      value,
      app: capturedApp,
    };
  } finally {
    DEFINED_APP_CAPTURE_STACK.pop();
  }
}

export async function captureDefinedRoutedAppDuring<Value>(
  action: () => Value | Promise<Value>,
): Promise<{ value: Value; app?: DefinedRoutedApp }> {
  const { value, app } = await captureDefinedAppDuring(action);
  return {
    value,
    app: app && isDefinedRoutedApp(app) ? app : undefined,
  };
}

function resolveGlobalDefinedAppCaptureStack(): Array<
  (app: DefinedApp) => void
> {
  const globalScope = globalThis as Record<PropertyKey, unknown>;
  const existing = globalScope[MAINZ_DEFINED_APP_CAPTURE_STACK_KEY];
  if (Array.isArray(existing)) {
    return existing as Array<(app: DefinedApp) => void>;
  }

  const created: Array<(app: DefinedApp) => void> = [];
  globalScope[MAINZ_DEFINED_APP_CAPTURE_STACK_KEY] = created;
  return created;
}

export function resolveRoutedAppDefinitionFromModuleExports(
  moduleExports: Record<string, unknown>,
): RoutedAppDefinition | undefined {
  return resolveRoutedAppDefinitionsFromModuleExports(moduleExports)[0];
}

export function resolveRoutedAppDefinitionsFromModuleExports(
  moduleExports: Record<string, unknown>,
): RoutedAppDefinition[] {
  const candidates = [
    moduleExports.default,
    moduleExports.app,
    ...Object.values(moduleExports),
  ];

  const resolved: RoutedAppDefinition[] = [];
  const seen = new Set<RoutedAppDefinition>();
  for (const candidate of candidates) {
    if (!isDefinedRoutedApp(candidate) || seen.has(candidate)) {
      continue;
    }

    seen.add(candidate);
    resolved.push(candidate);
  }

  return resolved;
}

export function resolveDefinedAppDefinitionsFromModuleExports(
  moduleExports: Record<string, unknown>,
): DefinedApp[] {
  const candidates = [
    moduleExports.default,
    moduleExports.app,
    ...Object.values(moduleExports),
  ];

  const resolved: DefinedApp[] = [];
  const seen = new Set<DefinedApp>();
  for (const candidate of candidates) {
    if (
      (!isDefinedRoutedApp(candidate) && !isDefinedRootApp(candidate)) ||
      seen.has(candidate)
    ) {
      continue;
    }

    seen.add(candidate);
    resolved.push(candidate);
  }

  return resolved;
}

/**
 * Starts a routed Mainz app created with {@link defineApp}.
 */
export function startApp(
  app: DefinedRoutedApp,
  options?: StartDefinedAppOptions,
): NavigationController;
/**
 * Starts a root-only Mainz app created with {@link defineApp}.
 */
export function startApp(
  app: DefinedRootApp,
  options?: StartDefinedAppOptions,
): NavigationController;
/**
 * Starts a root component directly without wrapping it in a root app definition.
 */
export function startApp(
  root: AppRootComponentConstructor,
  options?: StartDefinedAppOptions,
): NavigationController;
export function startApp(
  appOrRoot: DefinedRoutedApp | DefinedRootApp | AppRootComponentConstructor,
  options?: StartDefinedAppOptions,
): NavigationController {
  if (
    !isRootComponentConstructor(appOrRoot) &&
    !isDefinedRootApp(appOrRoot) &&
    !isDefinedRoutedApp(appOrRoot)
  ) {
    throw new TypeError(
      "startApp(...) for routed apps expects an app created with defineApp(...).",
    );
  }

  if (DEFINED_APP_CAPTURE_STACK.length > 0) {
    if (isDefinedRoutedApp(appOrRoot)) {
      captureDefinedApp(appOrRoot);
    }
    return {
      mode: resolveMainzNavigationMode(),
      cleanup() {},
    };
  }

  if (isRootComponentConstructor(appOrRoot)) {
    return registerNavigationController(startRootApp({
      id: appOrRoot.name || "root-app",
      root: appOrRoot,
    }, options));
  }

  if (isDefinedRootApp(appOrRoot)) {
    return registerNavigationController(startRootApp(appOrRoot, options));
  }

  if (!isDefinedRoutedApp(appOrRoot)) {
    throw new TypeError(
      "startApp(...) for routed apps expects an app created with defineApp(...).",
    );
  }

  captureDefinedApp(appOrRoot);
  if (appOrRoot.i18n?.dictionaries) {
    installAppI18n(appOrRoot.i18n);
  }

  return registerNavigationController(__internalStartNavigation({
    mode: resolveMainzNavigationMode(),
    basePath: resolveMainzBasePath(),
    appId: appOrRoot.id,
    commands: appOrRoot.commands,
    assets: appOrRoot.assets,
    mount: options?.mount,
    pages: appOrRoot.pages,
    notFound: appOrRoot.notFound,
    auth: options?.auth,
    services: appOrRoot.services,
    locales: appOrRoot.i18n?.locales,
  }));
}

function registerNavigationController(
  controller: NavigationController,
): NavigationController {
  if (typeof window === "undefined") {
    return controller;
  }

  const cleanupWindow = window as WindowWithMainzCleanupRegistry;
  const cleanupRegistry = cleanupWindow.__MAINZ_WINDOW_CLEANUPS__ ??= new Set();
  let active = true;
  const originalCleanup = controller.cleanup.bind(controller);

  const cleanup = () => {
    if (!active) {
      return;
    }

    active = false;
    cleanupRegistry.delete(cleanup);
    originalCleanup();
  };

  cleanupRegistry.add(cleanup);

  return {
    mode: controller.mode,
    cleanup,
  };
}

function startRootApp(
  app: Pick<
    RootAppDefinition,
    "assets" | "commands" | "id" | "root" | "services"
  >,
  options?: StartDefinedAppOptions,
): NavigationController {
  const mode = resolveMainzNavigationMode();
  if (typeof document === "undefined" || typeof window === "undefined") {
    return {
      mode,
      cleanup() {},
    };
  }

  setAuthorizationRuntimeOptions(options?.auth);
  document.documentElement.dataset.mainzNavigation = mode;

  const mount = resolveSpaMount(options?.mount);
  markMainzAppPortalRoot(mount);
  mount.setAttribute("data-mainz-app-id", app.id);
  const serviceContainer = app.services?.length
    ? createServiceContainer(app.services)
    : undefined;
  ensureAppCommandRegistry({
    root: mount,
    appId: app.id,
    commands: app.commands,
    services: serviceContainer,
  });
  applyResolvedAssetDefinitionsToDocument(resolveAssetDefinitions({
    appAssets: app.assets,
    context: createAssetContext({
      appId: app.id,
      phase: "client",
      renderMode: "csr",
      navigation: mode,
      path: window.location.pathname || "/",
      matchedPath: window.location.pathname || "/",
    }),
  }));

  ensureMainzCustomElementDefined(app.root);
  const rootElement = document.createElement(app.root.getTagName());
  attachServiceContainer(rootElement, serviceContainer);

  withServiceContainer(serviceContainer, () => {
    mount.replaceChildren(rootElement);
  });
  ensureDefaultAppPortalLayer(mount);

  return {
    mode,
    cleanup() {
      if (rootElement.parentNode === mount) {
        mount.removeChild(rootElement);
        cleanupAppCommandRegistry(mount);
        cleanupManagedAppPortalLayers(mount);
        return;
      }

      rootElement.remove();
      cleanupAppCommandRegistry(mount);
      cleanupManagedAppPortalLayers(mount);
    },
  };
}

function captureDefinedApp(app: DefinedApp): void {
  DEFINED_APP_CAPTURE_STACK.at(-1)?.(app);
}

function isRoutedAppDefinitionShape(
  value: unknown,
): value is RoutedAppDefinition {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const record = value as Record<string, unknown>;
  if (typeof record.id !== "string" || record.id.trim().length === 0) {
    return false;
  }

  if (!Array.isArray(record.pages) || !record.pages.every(isRoutedPageEntry)) {
    return false;
  }

  if (
    "notFound" in record && record.notFound !== undefined &&
    !isRoutedPageEntry(record.notFound)
  ) {
    return false;
  }

  return !("services" in record) || record.services === undefined ||
    Array.isArray(record.services);
}

function isRootAppDefinitionShape(value: unknown): value is RootAppDefinition {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const record = value as Record<string, unknown>;
  if (typeof record.id !== "string" || record.id.trim().length === 0) {
    return false;
  }

  if (!isRootComponentConstructor(record.root)) {
    return false;
  }

  return !("services" in record) || record.services === undefined ||
    Array.isArray(record.services);
}

function isRootComponentConstructor(
  value: unknown,
): value is AppRootComponentConstructor {
  return typeof value === "function" &&
    typeof (value as AppRootComponentConstructor).getTagName === "function";
}

function isRoutedPageEntry(value: unknown): boolean {
  if (typeof value === "function") {
    return true;
  }

  if (typeof value !== "object" || value === null) {
    return false;
  }

  return "page" in value || "load" in value;
}

function validateRoutedAppDefinition(app: RoutedAppDefinition): void {
  validateRoutedAppAuthorizationDefinition(app);
  validateRoutedAppI18nDefinition(app);
  validateImmediatePageLocaleRestrictions(app);
}

function validateRoutedAppAuthorizationDefinition(
  app: RoutedAppDefinition,
): void {
  const authorization = app.authorization;
  if (!authorization?.policyNames) {
    return;
  }

  if (!Array.isArray(authorization.policyNames)) {
    throw new Error(
      `App "${app.id}" authorization.policyNames must be an array.`,
    );
  }

  authorization.policyNames.forEach((policyName, index) => {
    if (typeof policyName !== "string") {
      throw new Error(
        `App "${app.id}" authorization.policyNames[${index}] must be a string.`,
      );
    }
  });
}

function validateRoutedAppI18nDefinition(app: RoutedAppDefinition): void {
  const i18n = app.i18n;
  if (!i18n) {
    return;
  }

  if (!Array.isArray(i18n.locales) || i18n.locales.length === 0) {
    throw new Error(`App "${app.id}" i18n.locales must not be empty.`);
  }

  const normalizedLocales = i18n.locales.map((locale, index) => {
    if (typeof locale !== "string") {
      throw new Error(
        `App "${app.id}" i18n.locales[${index}] must be a string.`,
      );
    }

    try {
      return normalizeLocaleTag(locale);
    } catch (error) {
      throw new Error(
        `App "${app.id}" i18n.locales[${index}] is not a valid locale tag: ${
          toErrorMessage(error)
        }`,
      );
    }
  });

  if (
    typeof i18n.defaultLocale !== "string" ||
    i18n.defaultLocale.trim().length === 0
  ) {
    throw new Error(
      `App "${app.id}" i18n.defaultLocale must be a string locale tag.`,
    );
  }

  const normalizedDefaultLocale = normalizeLocaleTag(i18n.defaultLocale);
  const localeSet = new Set(
    normalizedLocales.map((locale) => locale.toLowerCase()),
  );
  if (!localeSet.has(normalizedDefaultLocale.toLowerCase())) {
    throw new Error(
      `App "${app.id}" i18n.defaultLocale "${i18n.defaultLocale}" must be included in i18n.locales.`,
    );
  }

  if (
    i18n.localePrefix !== undefined &&
    i18n.localePrefix !== "always" &&
    i18n.localePrefix !== "except-default"
  ) {
    throw new Error(
      `App "${app.id}" i18n.localePrefix must be "always" or "except-default".`,
    );
  }

  if (i18n.dictionaries !== undefined) {
    if (typeof i18n.dictionaries !== "object" || i18n.dictionaries === null) {
      throw new Error(
        `App "${app.id}" i18n.dictionaries must be an object keyed by locale.`,
      );
    }

    for (const locale of i18n.locales) {
      if (!(locale in i18n.dictionaries)) {
        throw new Error(
          `App "${app.id}" i18n.dictionaries is missing locale "${locale}".`,
        );
      }
    }

    for (const locale of Object.keys(i18n.dictionaries)) {
      if (!i18n.locales.includes(locale as typeof i18n.locales[number])) {
        throw new Error(
          `App "${app.id}" i18n.dictionaries declares unsupported locale "${locale}".`,
        );
      }
    }
  }

  if (
    i18n.missing?.fallback !== undefined &&
    i18n.missing.fallback !== "default-locale"
  ) {
    const normalizedFallbackLocale = normalizeLocaleTag(i18n.missing.fallback);
    if (!localeSet.has(normalizedFallbackLocale.toLowerCase())) {
      throw new Error(
        `App "${app.id}" i18n.missing.fallback "${i18n.missing.fallback}" must be included in i18n.locales.`,
      );
    }
  }
}

function validateImmediatePageLocaleRestrictions(
  app: RoutedAppDefinition,
): void {
  const pageEntries = app.notFound
    ? [...app.pages, app.notFound]
    : [...app.pages];
  const appLocaleKeys = app.i18n
    ? new Set(
      app.i18n.locales.map((locale) =>
        normalizeLocaleTag(locale).toLowerCase()
      ),
    )
    : undefined;

  for (const page of collectImmediateNavigationPages(pageEntries)) {
    const pageLocales = resolvePageLocales(page);
    if (!pageLocales?.length) {
      continue;
    }

    if (!appLocaleKeys) {
      throw new Error(
        `App "${app.id}" page "${page.name}" declares @Locales(...) but the app does not define i18n.`,
      );
    }

    for (const pageLocale of pageLocales) {
      const normalizedPageLocale = normalizeLocaleTag(pageLocale);
      if (!appLocaleKeys.has(normalizedPageLocale.toLowerCase())) {
        throw new Error(
          `App "${app.id}" page "${page.name}" declares locale "${pageLocale}" outside app i18n.locales.`,
        );
      }
    }
  }
}

function isDefinedRoutedApp(value: unknown): value is InternalDefinedRoutedApp {
  return isRoutedAppDefinitionShape(value) &&
    readAppDefinitionKind(value) === "routed";
}

function isDefinedRootApp(value: unknown): value is InternalDefinedRootApp {
  return isRootAppDefinitionShape(value) &&
    readAppDefinitionKind(value) === "root";
}

function readAppDefinitionKind(value: unknown): "routed" | "root" | undefined {
  if (typeof value !== "object" || value === null) {
    return undefined;
  }

  return (value as Record<PropertyKey, unknown>)[MAINZ_APP_DEFINITION_KIND] as
    | "routed"
    | "root"
    | undefined;
}

function brandDefinedApp<TApp extends RoutedAppDefinition | RootAppDefinition>(
  app: TApp,
  kind: "routed" | "root",
): TApp {
  if (readAppDefinitionKind(app) !== kind) {
    Object.defineProperty(app, MAINZ_APP_DEFINITION_KIND, {
      value: kind,
      configurable: false,
      enumerable: false,
      writable: false,
    });
  }

  return app;
}

/**
 * Internal runtime bootstrap used by Mainz internals and focused framework tests.
 *
 * Application startup should prefer defineApp(...) + startApp(...).
 */
export function __internalStartNavigation(
  options: InternalStartNavigationOptions,
): NavigationController {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return {
      mode: options.mode,
      cleanup() {},
    };
  }

  setAuthorizationRuntimeOptions(options.auth ?? options.spa?.auth);

  const normalizedBasePath = normalizeNavigationBasePath(options.basePath);
  const pageOptions = resolvePageNavigationOptions(options);
  if (pageOptions) {
    pageOptions.appId = options.appId;
    pageOptions.commands = options.commands;
    pageOptions.serviceContainer = createServiceContainer(pageOptions.services);
    assertRegisteredNavigationPolicies(pageOptions);
  }

  document.documentElement.dataset.mainzNavigation = options.mode;
  const nextNavigationSequence = createNavigationSequenceSource();

  if (options.mode === "spa") {
    return startSpaNavigation(
      options.mode,
      normalizedBasePath,
      nextNavigationSequence,
      pageOptions,
    );
  }

  if (pageOptions) {
    void bootstrapDocumentNavigation(
      options.mode,
      pageOptions,
      normalizedBasePath,
      nextNavigationSequence,
    ).catch(
      reportSpaNavigationError,
    );
  }

  if (options.mode !== "mpa") {
    return {
      mode: options.mode,
      cleanup() {},
    };
  }

  return {
    mode: options.mode,
    cleanup() {},
  };
}

function startSpaNavigation(
  mode: NavigationMode,
  normalizedBasePath: string,
  nextNavigationSequence: NavigationSequenceSource,
  pageOptions?: ResolvedPageNavigationOptions,
): NavigationController {
  if (!pageOptions?.pages?.length && !pageOptions?.notFound) {
    return {
      mode,
      cleanup() {},
    };
  }

  const routes = normalizeSpaRoutes(pageOptions.pages, pageOptions.notFound);
  const mount = resolveSpaMount(pageOptions.mount);
  ensureDefaultAppPortalLayer(mount);
  ensureAppCommandRegistry({
    root: mount,
    appId: pageOptions.appId,
    commands: pageOptions.commands,
    services: pageOptions.serviceContainer,
  });
  let activeSequence: NavigationSequenceState | undefined;
  const initialUrl = resolveSpaLocalizedDocumentUrl(
    new URL(window.location.href),
    normalizedBasePath,
    pageOptions.locales,
  );
  if (initialUrl.toString() !== window.location.href) {
    window.history.replaceState({ mainzNavigation: "spa" }, "", initialUrl);
  }

  const initialSequence = createNavigationSequenceState(nextNavigationSequence);
  activeSequence = initialSequence;
  void renderSpaRoute({
    appId: pageOptions.appId,
    assets: pageOptions.assets,
    routes,
    mount,
    url: initialUrl,
    navigationType: "initial",
    basePath: normalizedBasePath,
    auth: pageOptions.auth,
    serviceContainer: pageOptions.serviceContainer,
    locales: pageOptions.locales,
    resolvePath: pageOptions.resolvePath,
    onLocaleChange: pageOptions.onLocaleChange,
    onRoute: pageOptions.onRoute,
    mode,
    nextNavigationSequence,
    sequence: initialSequence,
  }).catch(reportSpaNavigationError);

  const handleClick = (event: Event) => {
    if (!(event instanceof MouseEvent)) {
      return;
    }

    if (
      event.defaultPrevented || event.button !== 0 || event.metaKey ||
      event.ctrlKey ||
      event.shiftKey || event.altKey
    ) {
      return;
    }

    const anchor = findAnchorFromEvent(event);
    const targetUrl = resolveNavigableAnchorUrl(anchor, {
      basePath: normalizedBasePath,
    });
    if (!targetUrl) {
      return;
    }

    const currentUrl = new URL(window.location.href);
    if (isSameDocumentHashNavigation(currentUrl, targetUrl)) {
      return;
    }

    const effectiveTargetUrl = targetUrl;

    const targetPath = resolveRoutePath(
      effectiveTargetUrl,
      normalizedBasePath,
      pageOptions.resolvePath,
      pageOptions.locales,
    );
    if (!targetPath) {
      return;
    }

    const routeMatch = findMatchingSpaRoute(routes, targetPath);
    if (!routeMatch) {
      return;
    }

    event.preventDefault();

    abortNavigationSequence(activeSequence, {
      mount,
      mode,
      navigationType: "push",
      path: routeMatch.route.path,
      matchedPath: targetPath,
      locale: resolveNavigationLocale(
        effectiveTargetUrl,
        normalizedBasePath,
        pageOptions.locales,
      ),
      url: effectiveTargetUrl,
      basePath: normalizedBasePath,
      reason: "superseded",
    });

    const sequence = createNavigationSequenceState(nextNavigationSequence);
    activeSequence = sequence;
    let pendingNavigationReady: NavigationLifecycleEmissionArgs | undefined;
    void renderSpaRoute({
      appId: pageOptions.appId,
      assets: pageOptions.assets,
      routes,
      mount,
      url: effectiveTargetUrl,
      navigationType: "push",
      basePath: normalizedBasePath,
      routeMatch,
      auth: pageOptions.auth,
      serviceContainer: pageOptions.serviceContainer,
      locales: pageOptions.locales,
      resolvePath: pageOptions.resolvePath,
      onLocaleChange: pageOptions.onLocaleChange,
      onRoute: pageOptions.onRoute,
      mode,
      nextNavigationSequence,
      sequence,
      finalizeNavigationReady(readyArgs) {
        pendingNavigationReady = readyArgs;
      },
    })
      .then((rendered) => {
        if (!rendered) {
          return;
        }

        window.history.pushState(
          { mainzNavigation: "spa" },
          "",
          effectiveTargetUrl,
        );
        updateSpaScrollPosition(effectiveTargetUrl);
        if (pendingNavigationReady) {
          emitNavigationReady(pendingNavigationReady);
        }
      })
      .catch(reportSpaNavigationError);
  };

  const handlePopState = () => {
    const currentUrl = new URL(window.location.href);
    const effectiveCurrentUrl = currentUrl;
    const currentPath = resolveRoutePath(
      effectiveCurrentUrl,
      normalizedBasePath,
      pageOptions.resolvePath,
      pageOptions.locales,
    ) ?? "/";
    const routeMatch = findMatchingSpaRoute(routes, currentPath);
    abortNavigationSequence(activeSequence, {
      mount,
      mode,
      navigationType: "pop",
      path: routeMatch?.route.path ?? currentPath,
      matchedPath: currentPath,
      locale: resolveNavigationLocale(
        effectiveCurrentUrl,
        normalizedBasePath,
        pageOptions.locales,
      ),
      url: effectiveCurrentUrl,
      basePath: normalizedBasePath,
      reason: "superseded",
    });
    const sequence = createNavigationSequenceState(nextNavigationSequence);
    activeSequence = sequence;
    if (effectiveCurrentUrl.toString() !== currentUrl.toString()) {
      window.history.replaceState(
        { mainzNavigation: "spa" },
        "",
        effectiveCurrentUrl,
      );
    }
    void renderSpaRoute({
      appId: pageOptions.appId,
      assets: pageOptions.assets,
      routes,
      mount,
      url: effectiveCurrentUrl,
      navigationType: "pop",
      basePath: normalizedBasePath,
      auth: pageOptions.auth,
      serviceContainer: pageOptions.serviceContainer,
      locales: pageOptions.locales,
      resolvePath: pageOptions.resolvePath,
      onLocaleChange: pageOptions.onLocaleChange,
      onRoute: pageOptions.onRoute,
      mode,
      nextNavigationSequence,
      sequence,
    })
      .then((rendered) => {
        if (!rendered) {
          return;
        }

        updateSpaScrollPosition(effectiveCurrentUrl);
      })
      .catch(reportSpaNavigationError);
  };

  document.addEventListener("click", handleClick, { capture: true });
  window.addEventListener("popstate", handlePopState);

  return {
    mode,
    cleanup() {
      abortNavigationSequence(activeSequence, {
        mount,
        mode,
        navigationType: "initial",
        path: window.location.pathname,
        matchedPath: window.location.pathname,
        locale: undefined,
        url: new URL(window.location.href),
        basePath: normalizedBasePath,
        reason: "cleanup",
      });
      document.removeEventListener("click", handleClick, { capture: true });
      window.removeEventListener("popstate", handlePopState);
      cleanupAppCommandRegistry(mount);
    },
  };
}

async function renderSpaRoute(args: {
  appId?: string;
  assets?: readonly AssetDefinition[];
  routes: readonly NormalizedSpaRoute[];
  mount: HTMLElement;
  url: URL;
  navigationType: SpaNavigationRenderContext["navigationType"];
  basePath: string;
  routeMatch?: SpaRouteMatch;
  auth?: AuthorizationRuntimeOptions;
  serviceContainer?: ServiceContainer;
  locales?: readonly string[];
  resolvePath?: RoutePathResolver;
  onLocaleChange?: ResolvedPageNavigationOptions["onLocaleChange"];
  onRoute?: ResolvedPageNavigationOptions["onRoute"];
  mode: NavigationMode;
  nextNavigationSequence: NavigationSequenceSource;
  sequence?: NavigationSequenceState;
  finalizeNavigationReady?: (args: NavigationLifecycleEmissionArgs) => void;
}): Promise<boolean> {
  const currentPath = resolveRoutePath(
    args.url,
    args.basePath,
    args.resolvePath,
    args.locales,
  );
  if (!currentPath) {
    return false;
  }

  const routeMatch = args.routeMatch ??
    findMatchingSpaRoute(args.routes, currentPath);
  if (!routeMatch) {
    return false;
  }

  const locale = resolveNavigationLocale(args.url, args.basePath, args.locales);
  const sequence = args.sequence ??
    createNavigationSequenceState(args.nextNavigationSequence);
  emitNavigationStart({
    sequence,
    mount: args.mount,
    mode: args.mode,
    navigationType: args.navigationType,
    path: routeMatch.route.path,
    matchedPath: currentPath,
    locale,
    url: args.url,
    basePath: args.basePath,
  });

  let errorPhase: MainzNavigationErrorDetail["phase"] = "route-load";

  try {
    throwIfNavigationAborted(sequence);
    const page = await resolveSpaRoutePage(routeMatch.route);
    throwIfNavigationAborted(sequence);
    assertRegisteredPagePolicies(page, args.auth);
    ensurePageCustomElement(page);

    const pageTagName = page.getTagName();
    const authorization = resolvePageAuthorization(page);

    errorPhase = "authorization";
    const principal = await resolveCurrentPrincipal(args.auth);
    throwIfNavigationAborted(sequence);
    const accessDecision = await evaluatePageAuthorization({
      authorization,
      principal,
      policies: args.auth?.policies,
    });

    if (accessDecision.status === "redirect-login") {
      return await redirectUnauthorizedRouteToLogin({
        ...args,
        currentPath,
        locale,
        sequence,
      });
    }

    if (accessDecision.status === "forbidden") {
      applyNavigationLocale(
        locale,
        args.url,
        args.basePath,
        args.onLocaleChange,
      );
      renderForbiddenRoute(args.mount);
      finalizeNavigationReady({
        sequence,
        mount: args.mount,
        mode: args.mode,
        navigationType: args.navigationType,
        path: routeMatch.route.path,
        matchedPath: currentPath,
        locale,
        url: args.url,
        basePath: args.basePath,
      }, args.finalizeNavigationReady);
      return true;
    }

    const existingElement = args.mount.querySelector(pageTagName);
    const pageElement =
      args.navigationType === "initial" && isHtmlElement(existingElement)
        ? existingElement as RoutedPageElement
        : args.mount.ownerDocument.createElement(
          pageTagName,
        ) as RoutedPageElement;

    errorPhase = "route-load";
    const data = await resolvePageRouteData({
      page,
      pageElement,
      path: routeMatch.route.path,
      matchedPath: currentPath,
      params: routeMatch.params,
      locale,
      principal,
      url: args.url,
      signal: sequence.controller.signal,
      basePath: args.basePath,
      navigationMode: args.mode,
      serviceContainer: args.serviceContainer,
    });
    throwIfNavigationAborted(sequence);
    const metadata = resolveSpaRouteMetadata({
      page,
      pageElement,
      path: routeMatch.route.path,
      matchedPath: currentPath,
      locale,
      locales: args.locales,
      data,
      principal,
      url: args.url,
      basePath: args.basePath,
      navigationMode: args.mode,
    });
    const assets = resolveSpaRouteAssets({
      appId: args.appId,
      appAssets: args.assets,
      page,
      pageElement,
      path: routeMatch.route.path,
      matchedPath: currentPath,
      locale,
      principal,
      url: args.url,
      basePath: args.basePath,
      navigationMode: args.mode,
    });
    const routeContext = {
      page,
      path: routeMatch.route.path,
      matchedPath: currentPath,
      params: routeMatch.params,
      principal,
      authorization,
      data,
      metadata,
      assets,
      locale,
      url: args.url,
      navigationType: args.navigationType,
      basePath: args.basePath,
    } satisfies SpaNavigationRenderContext;
    attachServiceContainer(routeContext, args.serviceContainer);

    errorPhase = "page-render";
    throwIfNavigationAborted(sequence);
    applyNavigationLocale(locale, args.url, args.basePath, args.onLocaleChange);

    args.onRoute?.(routeContext);
    throwIfNavigationAborted(sequence);

    if (args.navigationType === "initial" && isHtmlElement(existingElement)) {
      applySpaRouteContext(existingElement, routeContext);
      applyResolvedPageMetadataToDocument(metadata);
      applyResolvedAssetDefinitionsToDocument(assets);
      finalizeNavigationReady({
        sequence,
        mount: args.mount,
        mode: args.mode,
        navigationType: args.navigationType,
        path: routeContext.path,
        matchedPath: routeContext.matchedPath,
        locale: routeContext.locale,
        url: routeContext.url,
        basePath: routeContext.basePath,
      }, args.finalizeNavigationReady);
      return true;
    }

    const nextPageElement = pageElement;
    applySpaRouteContext(nextPageElement, routeContext);
    args.mount.replaceChildren(nextPageElement);
    ensureDefaultAppPortalLayer(args.mount);
    applyResolvedPageMetadataToDocument(metadata);
    applyResolvedAssetDefinitionsToDocument(assets);
    finalizeNavigationReady({
      sequence,
      mount: args.mount,
      mode: args.mode,
      navigationType: args.navigationType,
      path: routeContext.path,
      matchedPath: routeContext.matchedPath,
      locale: routeContext.locale,
      url: routeContext.url,
      basePath: routeContext.basePath,
    }, args.finalizeNavigationReady);
    return true;
  } catch (error) {
    if (isAbortLikeError(error) || sequence.controller.signal.aborted) {
      return false;
    }
    emitNavigationError({
      sequence,
      mount: args.mount,
      mode: args.mode,
      navigationType: args.navigationType,
      path: routeMatch.route.path,
      matchedPath: currentPath,
      locale,
      url: args.url,
      basePath: args.basePath,
      phase: errorPhase,
      error,
    });
    throw error;
  }
}

function normalizeSpaRoutes(
  pages:
    readonly (SpaPageConstructor | SpaPageDefinition | SpaLazyPageDefinition)[],
  notFound?: SpaPageConstructor | SpaPageDefinition | SpaLazyPageDefinition,
): NormalizedSpaRoute[] {
  const routeEntries = [...pages];
  if (notFound) {
    routeEntries.push(normalizeSpaNotFoundRoute(notFound));
  }

  return routeEntries
    .map((entry) => normalizeSpaRoute(entry))
    .sort(compareSpaRoutesByPriority);
}

function normalizeSpaRoute(
  entry: SpaPageConstructor | SpaPageDefinition | SpaLazyPageDefinition,
): NormalizedSpaRoute {
  if (isSpaLazyPageDefinition(entry)) {
    const path = normalizeRoutePath(entry.path);
    if (!path) {
      throw new Error(
        "SPA lazy navigation pages must define an explicit route path.",
      );
    }

    return {
      path,
      load: entry.load,
    };
  }

  const page = isSpaPageDefinition(entry) ? entry.page : entry;
  const missingRouteMessage =
    `SPA navigation page "${page.name}" must define @Route(...) or an explicit route path.`;
  const path = normalizeRoutePath(
    isSpaPageDefinition(entry)
      ? entry.path ?? requirePageRoutePath(page, missingRouteMessage)
      : requirePageRoutePath(page, missingRouteMessage),
  );

  if (!path) {
    throw new Error(
      `SPA navigation page "${page.name}" must define @Route(...) or an explicit route path.`,
    );
  }

  return {
    page,
    path,
  };
}

function normalizeSpaNotFoundRoute(
  entry: SpaPageConstructor | SpaPageDefinition | SpaLazyPageDefinition,
): SpaPageDefinition | SpaLazyPageDefinition {
  if (isSpaLazyPageDefinition(entry)) {
    return {
      ...entry,
      path: "*",
    };
  }

  const page = isSpaPageDefinition(entry) ? entry.page : entry;

  return {
    page,
    path: "*",
  };
}

function isSpaPageDefinition(
  entry: SpaPageConstructor | SpaPageDefinition | SpaLazyPageDefinition,
): entry is SpaPageDefinition {
  return typeof entry === "object" && entry !== null && "page" in entry;
}

function isSpaLazyPageDefinition(
  entry: SpaPageConstructor | SpaPageDefinition | SpaLazyPageDefinition,
): entry is SpaLazyPageDefinition {
  return typeof entry === "object" && entry !== null && "load" in entry;
}

function compareSpaRoutesByPriority(
  a: NormalizedSpaRoute,
  b: NormalizedSpaRoute,
): number {
  const aStats = getRoutePatternStats(a.path);
  const bStats = getRoutePatternStats(b.path);

  if (aStats.staticCount !== bStats.staticCount) {
    return bStats.staticCount - aStats.staticCount;
  }

  if (aStats.dynamicCount !== bStats.dynamicCount) {
    return aStats.dynamicCount - bStats.dynamicCount;
  }

  if (aStats.hasCatchAll !== bStats.hasCatchAll) {
    return aStats.hasCatchAll ? 1 : -1;
  }

  if (aStats.depth !== bStats.depth) {
    return bStats.depth - aStats.depth;
  }

  return a.path.localeCompare(b.path);
}

function getRoutePatternStats(path: string): {
  staticCount: number;
  dynamicCount: number;
  hasCatchAll: boolean;
  depth: number;
} {
  const segments = getRouteSegments(path);
  let staticCount = 0;
  let dynamicCount = 0;
  let hasCatchAll = false;

  for (const segment of segments) {
    if (segment === "*" || segment.startsWith("[...")) {
      hasCatchAll = true;
      dynamicCount += 1;
      continue;
    }

    if (segment.startsWith(":") || isBracketDynamicSegment(segment)) {
      dynamicCount += 1;
      continue;
    }

    staticCount += 1;
  }

  return {
    staticCount,
    dynamicCount,
    hasCatchAll,
    depth: segments.length,
  };
}

function findMatchingSpaRoute(
  routes: readonly NormalizedSpaRoute[],
  currentPath: string,
): SpaRouteMatch | undefined {
  for (const route of routes) {
    const params = matchRoutePath(route.path, currentPath);
    if (!params) {
      continue;
    }

    return {
      route,
      params,
    };
  }

  return undefined;
}

function matchRoutePath(
  routePath: string,
  currentPath: string,
): Record<string, string> | null {
  const routeSegments = getRouteSegments(routePath);
  const currentSegments = getRouteSegments(currentPath);
  const params: Record<string, string> = {};

  let routeIndex = 0;
  let currentIndex = 0;

  while (
    routeIndex < routeSegments.length && currentIndex < currentSegments.length
  ) {
    const routeSegment = routeSegments[routeIndex];
    const currentSegment = currentSegments[currentIndex];

    if (routeSegment === "*") {
      params["*"] = currentSegments.slice(currentIndex).map(
        decodeRouteParamSegment,
      ).join(
        "/",
      );
      return params;
    }

    if (routeSegment.startsWith("[...")) {
      params[routeSegment.slice(4, -1)] = currentSegments.slice(currentIndex)
        .map(
          decodeRouteParamSegment,
        ).join("/");
      return params;
    }

    if (routeSegment.startsWith(":")) {
      params[routeSegment.slice(1)] = decodeRouteParamSegment(currentSegment);
      routeIndex += 1;
      currentIndex += 1;
      continue;
    }

    if (isBracketDynamicSegment(routeSegment)) {
      params[routeSegment.slice(1, -1)] = decodeRouteParamSegment(
        currentSegment,
      );
      routeIndex += 1;
      currentIndex += 1;
      continue;
    }

    if (routeSegment !== currentSegment) {
      return null;
    }

    routeIndex += 1;
    currentIndex += 1;
  }

  if (
    routeIndex === routeSegments.length &&
    currentIndex === currentSegments.length
  ) {
    return params;
  }

  if (
    routeIndex === routeSegments.length - 1 && routeSegments[routeIndex] === "*"
  ) {
    params["*"] = currentSegments.slice(currentIndex).map(
      decodeRouteParamSegment,
    ).join("/");
    return params;
  }

  if (
    routeIndex === routeSegments.length - 1 &&
    routeSegments[routeIndex].startsWith("[...")
  ) {
    params[routeSegments[routeIndex].slice(4, -1)] = currentSegments.slice(
      currentIndex,
    )
      .map(decodeRouteParamSegment)
      .join("/");
    return params;
  }

  return null;
}

function resolveSpaMount(mount?: string | Element): HTMLElement {
  if (isHtmlElement(mount)) {
    return mount;
  }

  const selector = typeof mount === "string" ? mount : "#app";
  const resolved = document.querySelector(selector);
  if (!isHtmlElement(resolved)) {
    throw new Error(
      `SPA navigation could not find the mount element "${selector}".`,
    );
  }

  return resolved;
}

function ensurePageCustomElement(page: SpaPageConstructor): void {
  ensureMainzCustomElementDefined(
    page as unknown as CustomElementConstructor & { getTagName(): string },
  );
}

async function resolveSpaRoutePage(
  route: NormalizedSpaRoute,
): Promise<SpaPageConstructor> {
  if (route.page) {
    return route.page;
  }

  if (!route.load) {
    throw new Error(
      `SPA route "${route.path}" has no page constructor or lazy loader.`,
    );
  }

  const loadedPage = resolveSpaPageModule(await route.load());
  route.page = loadedPage;
  return loadedPage;
}

function resolveSpaPageModule(module: SpaPageModule): SpaPageConstructor {
  if (typeof module === "function") {
    return module;
  }

  if (
    module && typeof module === "object" && typeof module.default === "function"
  ) {
    return module.default;
  }

  throw new Error(
    "SPA lazy navigation loader must resolve to a Page constructor or { default: Page }.",
  );
}

function applySpaRouteContext(
  element: HTMLElement,
  context: SpaNavigationRenderContext,
): void {
  const serviceContainer = readServiceContainer(context);
  const routeContext = attachServiceContainer(
    createRouteContext({
      path: context.path,
      matchedPath: context.matchedPath,
      params: context.params,
      locale: context.locale,
      url: context.url,
      renderMode: resolveSpaPageRenderMode(context.page),
      navigationMode: resolveMainzNavigationMode(),
      principal: context.principal,
      profile: createRouteProfileContext(context.basePath),
    }),
    serviceContainer,
  );

  const pageElement = element as RoutedPageElement;
  pageElement.data = context.data;
  pageElement.props = {
    ...readSpaElementProps(element),
    route: routeContext,
    data: context.data,
    metadata: context.metadata,
    assets: context.assets,
  };
  attachServiceContainer(element, serviceContainer);

  const rerender =
    (element as HTMLElement & { rerender?: () => void }).rerender;
  if (typeof rerender === "function") {
    rerender.call(element);
  }
}

function readSpaElementProps(element: HTMLElement): Record<string, unknown> {
  const currentProps = (element as HTMLElement & { props?: unknown }).props;
  if (typeof currentProps === "object" && currentProps !== null) {
    return currentProps as Record<string, unknown>;
  }

  return {};
}

function isHtmlElement(value: unknown): value is HTMLElement {
  if (!value || typeof value !== "object") {
    return false;
  }

  const ownerDocument = "ownerDocument" in value
    ? (value as { ownerDocument?: Document | null }).ownerDocument
    : undefined;
  const ownerWindow = ownerDocument?.defaultView;
  const ownerHTMLElement = ownerWindow?.HTMLElement;

  if (ownerHTMLElement) {
    return value instanceof ownerHTMLElement;
  }

  const ownerElement = ownerWindow?.Element;
  if (ownerElement) {
    return value instanceof ownerElement;
  }

  return false;
}

function applyResolvedPageMetadataToDocument(
  metadataDefinition: PageMetadataDefinition | undefined,
): void {
  if (typeof document === "undefined") {
    return;
  }

  const head = document.head;
  if (!head) {
    return;
  }

  head.querySelectorAll(`[${MAINZ_METADATA_MANAGED_ATTR}]`).forEach((node) =>
    node.remove()
  );

  if (!metadataDefinition) {
    return;
  }

  if (metadataDefinition.title) {
    document.title = metadataDefinition.title;
  }

  for (const meta of metadataDefinition.meta ?? []) {
    const element = document.createElement("meta");
    if (meta.name) {
      element.setAttribute("name", meta.name);
    }
    if (meta.property) {
      element.setAttribute("property", meta.property);
    }
    element.setAttribute("content", meta.content);
    element.setAttribute(MAINZ_METADATA_MANAGED_ATTR, "true");
    head.appendChild(element);
  }

  for (const link of metadataDefinition.links ?? []) {
    const element = document.createElement("link");
    element.setAttribute("rel", link.rel);
    element.setAttribute("href", link.href);
    if (link.hreflang) {
      element.setAttribute("hreflang", link.hreflang);
    }
    element.setAttribute(MAINZ_METADATA_MANAGED_ATTR, "true");
    head.appendChild(element);
  }
}

function resolveSpaRouteMetadata(args: {
  page: SpaPageConstructor;
  pageElement: RoutedPageElement;
  path: string;
  matchedPath: string;
  locale?: string;
  locales?: readonly string[];
  data?: unknown;
  principal?: Principal;
  url: URL;
  basePath: string;
  navigationMode: NavigationMode;
}): PageMetadataDefinition | undefined {
  const routeContext = createRouteContext({
    path: args.path,
    matchedPath: args.matchedPath,
    params: resolveRouteParamsFromPageElement(args.pageElement),
    locale: args.locale,
    url: args.url,
    renderMode: resolveSpaPageRenderMode(args.page),
    navigationMode: args.navigationMode,
    principal: args.principal,
    profile: createRouteProfileContext(args.basePath),
  });
  const metadataContext = createPageLoadContext({
    path: args.path,
    matchedPath: args.matchedPath,
    params: routeContext.params,
    locale: args.locale,
    url: args.url,
    renderMode: routeContext.renderMode,
    navigationMode: args.navigationMode,
    principal: args.principal,
    profile: routeContext.profile,
  });
  const instanceMetadata = typeof args.pageElement.metadata === "function"
    ? args.pageElement.metadata.call(args.pageElement, metadataContext)
    : undefined;
  const mergedMetadata = instanceMetadata;
  const routeLocales = resolveSpaRouteLocales(args.page, args.locales);
  const activeLocale = args.locale ?? routeLocales[0] ??
    resolveMainzDefaultLocale();
  if (!activeLocale) {
    return mergedMetadata;
  }

  return buildRouteMetadata({
    path: args.matchedPath,
    locale: activeLocale,
    locales: routeLocales,
    metadata: mergedMetadata,
    localePrefix: resolveMainzLocalePrefix(),
    defaultLocale: resolveMainzDefaultLocale() ?? routeLocales[0],
    basePath: resolveMainzBasePath(),
    siteUrl: resolveMainzSiteUrl(),
  });
}

function resolveSpaRouteAssets(args: {
  appId?: string;
  appAssets?: readonly AssetDefinition[];
  page: SpaPageConstructor;
  pageElement: RoutedPageElement;
  path: string;
  matchedPath: string;
  locale?: string;
  principal?: Principal;
  url: URL;
  basePath: string;
  navigationMode: NavigationMode;
}): readonly AssetDefinition[] | undefined {
  const assetContext = createAssetContext({
    appId: args.appId,
    phase: "client",
    renderMode: resolveSpaPageRenderMode(args.page),
    navigation: args.navigationMode,
    path: args.path,
    matchedPath: args.matchedPath,
    locale: args.locale,
  });
  const pageContext = createPageLoadContext({
    path: args.path,
    matchedPath: args.matchedPath,
    params: resolveRouteParamsFromPageElement(args.pageElement),
    locale: args.locale,
    url: args.url,
    renderMode: resolveSpaPageRenderMode(args.page),
    navigationMode: args.navigationMode,
    principal: args.principal,
    profile: createRouteProfileContext(args.basePath),
  });
  const instanceAssets = typeof args.pageElement.assets === "function"
    ? args.pageElement.assets.call(args.pageElement, pageContext)
    : undefined;
  const resolvedAssets = resolveAssetDefinitions({
    appAssets: args.appAssets,
    pageAssets: instanceAssets,
    context: assetContext,
  });

  return resolvedAssets.length > 0 ? resolvedAssets : undefined;
}

function resolveSpaRouteLocales(
  page: SpaPageConstructor,
  fallbackLocales?: readonly string[],
): readonly string[] {
  const pageLocales = resolvePageLocales(page);
  if (pageLocales?.length) {
    return pageLocales;
  }

  if (fallbackLocales?.length) {
    return fallbackLocales;
  }

  const appLocales = readMainzAppLocales();
  return appLocales.length > 0 ? appLocales : [];
}

async function resolvePageRouteData(args: {
  page: SpaPageConstructor;
  pageElement: RoutedPageElement;
  path: string;
  matchedPath: string;
  params: SpaRouteParams;
  locale?: string;
  principal?: Principal;
  url: URL;
  signal: AbortSignal;
  basePath: string;
  navigationMode: NavigationMode;
  serviceContainer?: ServiceContainer;
}): Promise<unknown> {
  const routeProfile = createRouteProfileContext(args.basePath);
  const routeContext = createRouteContext({
    path: args.path,
    matchedPath: args.matchedPath,
    params: args.params,
    locale: args.locale,
    url: args.url,
    renderMode: resolveSpaPageRenderMode(args.page),
    navigationMode: args.navigationMode,
    principal: args.principal,
    profile: routeProfile,
  });
  applyPageLifecycleProps(args.pageElement, {
    route: routeContext,
    data: args.pageElement.data,
    metadata: readResolvedPageMetadataFromProps(args.pageElement.props),
    assets: readResolvedPageAssetsFromProps(args.pageElement.props),
  });
  attachServiceContainer(args.pageElement, args.serviceContainer);

  const hasInstanceLoad = typeof args.pageElement.load === "function";
  const hasStaticLoad =
    typeof Reflect.get(args.page as object, "load") === "function";

  if (hasStaticLoad) {
    throw new Error(
      `Page "${args.page.name}" declares static load(), which is no longer supported. Move that logic into the page instance load() lifecycle.`,
    );
  }

  if (!hasInstanceLoad) {
    return undefined;
  }

  if (args.signal.aborted) {
    throw new NavigationAbortedError();
  }

  const context: PageLoadContext = createPageLoadContext({
    path: args.path,
    matchedPath: args.matchedPath,
    params: args.params,
    locale: args.locale,
    url: args.url,
    renderMode: resolveSpaPageRenderMode(args.page),
    navigationMode: args.navigationMode,
    signal: args.signal,
    principal: args.principal,
    profile: routeProfile,
  });

  if (hasInstanceLoad) {
    const result = await withServiceContainer(
      args.serviceContainer,
      () => args.pageElement.load!.call(args.pageElement, context),
    );
    args.pageElement.data = result;
    applyPageLifecycleProps(args.pageElement, {
      route: routeContext,
      data: result,
      metadata: readResolvedPageMetadataFromProps(args.pageElement.props),
      assets: readResolvedPageAssetsFromProps(args.pageElement.props),
    });
    return result;
  }
}

function createRouteContext(args: {
  path: string;
  matchedPath: string;
  params: SpaRouteParams;
  locale?: string;
  url: URL;
  renderMode: PageRenderMode;
  navigationMode: NavigationMode;
  principal?: Principal;
  profile?: RouteProfileContext;
}): RouteContext {
  return {
    path: args.path,
    matchedPath: args.matchedPath,
    params: args.params,
    locale: args.locale,
    url: args.url,
    renderMode: args.renderMode,
    navigationMode: args.navigationMode,
    principal: args.principal,
    profile: args.profile,
  };
}

function createRouteProfileContext(basePath: string): RouteProfileContext {
  return {
    basePath,
    siteUrl: resolveMainzSiteUrl(),
  };
}

function applyPageLifecycleProps(
  element: RoutedPageElement,
  args: {
    route: RouteContext;
    data?: unknown;
    metadata?: PageMetadataDefinition;
    assets?: readonly AssetDefinition[];
  },
): void {
  const nextProps = {
    ...(element.props ?? {}),
    route: args.route,
    data: args.data,
    metadata: args.metadata,
    assets: args.assets,
  };

  element.props = nextProps;
}

function readResolvedPageMetadataFromProps(
  props: unknown,
): PageMetadataDefinition | undefined {
  if (typeof props !== "object" || props === null) {
    return undefined;
  }

  const propsRecord = props as Record<string, unknown>;
  return isPageMetadataDefinition(propsRecord.metadata)
    ? propsRecord.metadata
    : undefined;
}

function readResolvedPageAssetsFromProps(
  props: unknown,
): readonly AssetDefinition[] | undefined {
  if (typeof props !== "object" || props === null) {
    return undefined;
  }

  const propsRecord = props as Record<string, unknown>;
  return isAssetDefinitionList(propsRecord.assets)
    ? propsRecord.assets
    : undefined;
}

function resolveRouteParamsFromPageElement(
  element: RoutedPageElement,
): SpaRouteParams {
  if (typeof element.props !== "object" || element.props === null) {
    return {};
  }

  const route = (element.props as Record<string, unknown>).route;
  if (typeof route !== "object" || route === null) {
    return {};
  }

  const params = (route as Record<string, unknown>).params;
  return isStringRecord(params) ? params : {};
}

async function redirectUnauthorizedRouteToLogin(args: {
  appId?: string;
  assets?: readonly AssetDefinition[];
  routes: readonly NormalizedSpaRoute[];
  mount: HTMLElement;
  url: URL;
  navigationType: SpaNavigationRenderContext["navigationType"];
  basePath: string;
  currentPath: string;
  locale?: string;
  auth?: AuthorizationRuntimeOptions;
  serviceContainer?: ServiceContainer;
  locales?: readonly string[];
  resolvePath?: RoutePathResolver;
  onLocaleChange?: ResolvedPageNavigationOptions["onLocaleChange"];
  onRoute?: ResolvedPageNavigationOptions["onRoute"];
  mode: NavigationMode;
  nextNavigationSequence: NavigationSequenceSource;
  sequence: NavigationSequenceState;
}): Promise<boolean> {
  const loginPath = normalizeRoutePath(args.auth?.loginPath ?? "/login");
  if (!loginPath || loginPath === args.currentPath) {
    applyNavigationLocale(
      args.locale,
      args.url,
      args.basePath,
      args.onLocaleChange,
    );
    renderForbiddenRoute(args.mount);
    emitNavigationReady({
      sequence: args.sequence,
      mount: args.mount,
      mode: args.mode,
      navigationType: args.navigationType,
      path: args.currentPath,
      matchedPath: args.currentPath,
      locale: args.locale,
      url: args.url,
      basePath: args.basePath,
    });
    return true;
  }

  const redirectUrl = buildAuthorizationRedirectUrl({
    loginPath,
    currentUrl: args.url,
    basePath: args.basePath,
    locale: args.locale,
    locales: args.locales,
  });

  if (args.navigationType === "push") {
    window.history.pushState({ mainzNavigation: "spa" }, "", redirectUrl);
  } else {
    window.history.replaceState({ mainzNavigation: "spa" }, "", redirectUrl);
  }

  updateSpaScrollPosition(redirectUrl);

  await renderSpaRoute({
    appId: args.appId,
    assets: args.assets,
    routes: args.routes,
    mount: args.mount,
    url: redirectUrl,
    navigationType: args.navigationType,
    basePath: args.basePath,
    auth: args.auth,
    serviceContainer: args.serviceContainer,
    locales: args.locales,
    resolvePath: args.resolvePath,
    onLocaleChange: args.onLocaleChange,
    onRoute: args.onRoute,
    mode: args.mode,
    nextNavigationSequence: args.nextNavigationSequence,
    sequence: args.sequence,
  });

  return false;
}

function buildAuthorizationRedirectUrl(args: {
  loginPath: string;
  currentUrl: URL;
  basePath: string;
  locale?: string;
  locales?: readonly string[];
}): URL {
  const redirectUrl = new URL(args.currentUrl.toString());
  redirectUrl.pathname = joinNavigationBasePath(
    args.basePath,
    buildLocalizedNavigationPath(args.loginPath, args.locale, args.locales),
  );
  redirectUrl.search = "";
  redirectUrl.hash = "";
  return redirectUrl;
}

function buildLocalizedNavigationPath(
  routePath: string,
  locale: string | undefined,
  locales: readonly string[] | undefined,
): string {
  const normalizedRoutePath = normalizeRoutePath(routePath) ?? "/";
  const shouldPrefixLocale = Boolean(
    locale &&
      locales?.length &&
      shouldPrefixNavigationLocale(locale, locales),
  );

  if (!shouldPrefixLocale || !locale) {
    return normalizedRoutePath;
  }

  const localeSegment = toLocalePathSegment(locale);

  if (normalizedRoutePath === "/") {
    return `/${localeSegment}/`;
  }

  return `/${localeSegment}${normalizedRoutePath}`;
}

function renderForbiddenRoute(mount: HTMLElement): void {
  const element = document.createElement("section");
  element.setAttribute("data-mainz-authorization", "forbidden");
  element.setAttribute("data-mainz-status", "403");
  element.textContent = "403 Forbidden";
  mount.replaceChildren(element);
  ensureDefaultAppPortalLayer(mount);
  applyResolvedPageMetadataToDocument({
    title: "403 Forbidden",
  });
  applyResolvedAssetDefinitionsToDocument(undefined);
}

function updateSpaScrollPosition(url: URL): void {
  if (url.hash) {
    const anchorTarget = document.getElementById(
      decodeURIComponent(url.hash.slice(1)),
    );
    anchorTarget?.scrollIntoView();
    return;
  }

  window.scrollTo(0, 0);
}

function resolveNavigableAnchorUrl(
  anchor: HTMLAnchorElement | null | undefined,
  options: NavigationAnchorOptions = {},
): URL | null {
  if (!anchor) {
    return null;
  }

  const href = anchor.getAttribute("href")?.trim();
  if (!href || href.startsWith("#")) {
    return null;
  }

  if (anchor.hasAttribute("download")) {
    return null;
  }

  const target = anchor.getAttribute("target");
  if (target && target !== "_self") {
    return null;
  }

  let resolvedUrl: URL;
  try {
    resolvedUrl = new URL(anchor.href, window.location.href);
  } catch {
    return null;
  }

  if (resolvedUrl.origin !== window.location.origin) {
    return null;
  }

  if (
    !isUrlWithinNavigationBasePath(
      resolvedUrl,
      normalizeNavigationBasePath(options.basePath),
    )
  ) {
    return null;
  }

  return resolvedUrl;
}

function findAnchorFromEvent(event: Event): HTMLAnchorElement | null {
  const target = event.target;
  if (!(target instanceof Element)) {
    return null;
  }

  return target.closest("a[href]") as HTMLAnchorElement | null;
}

function isSameDocumentHashNavigation(
  currentUrl: URL,
  targetUrl: URL,
): boolean {
  return currentUrl.pathname === targetUrl.pathname &&
    currentUrl.search === targetUrl.search &&
    currentUrl.hash !== targetUrl.hash;
}

function toAppRelativePath(url: URL, basePath: string): string | null {
  if (!isUrlWithinNavigationBasePath(url, basePath)) {
    return null;
  }

  if (basePath === "/") {
    return normalizeRoutePath(url.pathname) ?? "/";
  }

  const basePathWithoutTrailingSlash = basePath.slice(0, -1);
  if (url.pathname === basePathWithoutTrailingSlash) {
    return "/";
  }

  return normalizeRoutePath(
    url.pathname.slice(basePathWithoutTrailingSlash.length),
  ) ?? "/";
}

function normalizeNavigationBasePath(basePath?: string): string {
  const normalizedBasePath = (basePath ?? "/").trim();

  if (
    !normalizedBasePath || normalizedBasePath === "." ||
    normalizedBasePath === "./"
  ) {
    return "/";
  }

  const withLeadingSlash = normalizedBasePath.startsWith("/")
    ? normalizedBasePath
    : `/${normalizedBasePath}`;
  return withLeadingSlash.endsWith("/")
    ? withLeadingSlash
    : `${withLeadingSlash}/`;
}

function isUrlWithinNavigationBasePath(url: URL, basePath: string): boolean {
  if (basePath === "/") {
    return true;
  }

  const basePathWithoutTrailingSlash = basePath.slice(0, -1);
  return url.pathname === basePathWithoutTrailingSlash ||
    url.pathname.startsWith(basePath);
}

function normalizeRoutePath(path: string | undefined): string | null {
  const trimmed = path?.trim();
  if (!trimmed) {
    return null;
  }

  const withLeadingSlash = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  const normalizedSlashes = withLeadingSlash.replace(/\/{2,}/g, "/");

  if (normalizedSlashes.length > 1 && normalizedSlashes.endsWith("/")) {
    return normalizedSlashes.slice(0, -1);
  }

  return normalizedSlashes;
}

function getRouteSegments(path: string): string[] {
  return normalizeRoutePath(path)?.split("/").filter(Boolean) ?? [];
}

function isBracketDynamicSegment(segment: string): boolean {
  return /^\[[^\].]+\]$/.test(segment);
}

function decodeRouteParamSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

function reportSpaNavigationError(error: unknown): void {
  console.error("[mainz] SPA navigation failed.", error);
}

function toErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  return String(error);
}

function resolveSpaLocalizedDocumentUrl(
  url: URL,
  basePath: string,
  locales?: readonly string[],
): URL {
  const redirectLocale = resolveSpaRootRedirectLocale(url, basePath, locales);
  if (!redirectLocale) {
    return url;
  }

  const nextUrl = new URL(url.toString());
  nextUrl.pathname = joinNavigationBasePath(
    basePath,
    buildLocalizedNavigationPath("/", redirectLocale, locales),
  );
  return nextUrl;
}

function resolveSpaRootRedirectLocale(
  url: URL,
  basePath: string,
  locales?: readonly string[],
): string | undefined {
  if (!locales?.length) {
    return undefined;
  }

  const appPath = toAppRelativePath(url, basePath);
  if (appPath && appPath !== "/") {
    return undefined;
  }

  const redirectPath = resolveLocaleRedirectPath({
    supportedLocales: locales,
    defaultLocale: resolveMainzDefaultLocale() ?? locales[0],
    preferredLocales: readPreferredNavigationLocales(),
  });
  const localeSegment = redirectPath.split("/").filter(Boolean)[0];
  const locale = localeSegment
    ? locales.find((candidate) =>
      toLocalePathSegment(candidate) === localeSegment
    )
    : undefined;
  if (!locale || !shouldPrefixNavigationLocale(locale, locales)) {
    return undefined;
  }

  return locale;
}

function joinNavigationBasePath(basePath: string, routePath: string): string {
  if (basePath === "/") {
    return routePath;
  }

  const normalizedRoutePath = routePath.startsWith("/")
    ? routePath
    : `/${routePath}`;
  return `${basePath.slice(0, -1)}${normalizedRoutePath}`;
}

function readPreferredNavigationLocales(): readonly string[] {
  const preferredLocales: string[] = [];

  if (typeof navigator === "undefined") {
    return preferredLocales;
  }

  if (Array.isArray(navigator.languages)) {
    preferredLocales.push(
      ...navigator.languages.filter((value): value is string =>
        typeof value === "string"
      ),
    );
  }

  if (typeof navigator.language === "string" && navigator.language.trim()) {
    preferredLocales.push(navigator.language);
  }

  return preferredLocales;
}

function applyNavigationLocale(
  locale: string | undefined,
  url: URL,
  basePath: string,
  onLocaleChange?: ResolvedPageNavigationOptions["onLocaleChange"],
): void {
  if (!locale) {
    return;
  }

  if (typeof document !== "undefined") {
    document.documentElement.lang = locale;
    document.dispatchEvent(
      new CustomEvent<MainzLocaleChangeDetail>(MAINZ_LOCALE_CHANGE_EVENT, {
        detail: {
          locale,
          url: url.toString(),
          basePath,
        },
      }),
    );
  }

  onLocaleChange?.({
    locale,
    url,
    basePath,
  });
}

function resolveRoutePath(
  url: URL,
  basePath: string,
  resolver?: RoutePathResolver,
  locales?: readonly string[],
): string | null {
  if (resolver) {
    const resolvedPath = resolver({ url, basePath });
    return normalizeRoutePath(resolvedPath ?? undefined) ?? "/";
  }

  const appPath = toAppRelativePath(url, basePath);
  if (!appPath) {
    return null;
  }

  return stripLocalePrefixFromPath(appPath, locales);
}

function resolveNavigationLocale(
  url: URL,
  basePath: string,
  locales?: readonly string[],
): string | undefined {
  if (!locales?.length) {
    return undefined;
  }

  const appPath = toAppRelativePath(url, basePath) ?? "/";
  const firstSegment = appPath.split("/").filter(Boolean)[0];
  const defaultLocale = resolveDefaultNavigationLocale(locales);

  if (firstSegment) {
    const matchedLocale = locales.find((locale) =>
      locale.toLowerCase() === firstSegment.toLowerCase()
    );
    if (matchedLocale) {
      return matchedLocale;
    }

    if (resolveMainzLocalePrefix() === "except-default") {
      return defaultLocale;
    }
  }

  if (resolveMainzLocalePrefix() === "except-default") {
    return defaultLocale;
  }

  const documentLocale = typeof document === "undefined"
    ? ""
    : document.documentElement.lang.trim();
  if (!documentLocale) {
    return defaultLocale;
  }

  return locales.find((locale) =>
    locale.toLowerCase() === documentLocale.toLowerCase()
  ) ??
    defaultLocale;
}

function shouldPrefixNavigationLocale(
  locale: string,
  locales: readonly string[] | undefined,
): boolean {
  if (!locales?.length) {
    return false;
  }

  const localePrefix = resolveMainzLocalePrefix();
  if (localePrefix === "always") {
    return true;
  }

  const defaultLocale = resolveDefaultNavigationLocale(locales);
  return normalizeLocaleTag(locale).toLowerCase() !==
    normalizeLocaleTag(defaultLocale).toLowerCase();
}

function resolveDefaultNavigationLocale(locales: readonly string[]): string {
  const configuredDefault = resolveMainzDefaultLocale();
  if (configuredDefault) {
    const matchedDefault = locales.find((locale) =>
      normalizeLocaleTag(locale).toLowerCase() ===
        normalizeLocaleTag(configuredDefault).toLowerCase()
    );
    if (matchedDefault) {
      return matchedDefault;
    }
  }

  return locales[0];
}

function stripLocalePrefixFromPath(
  pathname: string,
  locales?: readonly string[],
): string {
  if (!locales?.length) {
    return pathname || "/";
  }

  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) {
    return "/";
  }

  const [first, ...rest] = segments;
  const isLocaleSegment = locales.some((locale) =>
    locale.toLowerCase() === first.toLowerCase()
  );
  if (!isLocaleSegment) {
    return pathname || "/";
  }

  return rest.length === 0 ? "/" : `/${rest.join("/")}`;
}

function resolvePageNavigationOptions(
  options: InternalStartNavigationOptions,
): ResolvedPageNavigationOptions | undefined {
  const legacySpaOptions = options.spa;
  const assets = options.assets;
  const pages = options.pages ?? legacySpaOptions?.pages;
  const notFound = options.notFound ?? legacySpaOptions?.notFound;
  const mount = options.mount ?? legacySpaOptions?.mount;
  const auth = options.auth ?? legacySpaOptions?.auth;
  const services = options.services ?? legacySpaOptions?.services;
  const locales = options.locales ?? legacySpaOptions?.locales;
  const resolvePath = options.resolvePath ?? legacySpaOptions?.resolvePath;
  const onLocaleChange = options.onLocaleChange ??
    legacySpaOptions?.onLocaleChange;
  const onRoute = options.onRoute ??
    options.onBeforeRender ??
    legacySpaOptions?.onRoute ??
    legacySpaOptions?.onBeforeRender;

  if (!pages?.length && !notFound && !mount && !onRoute) {
    return undefined;
  }

  return {
    assets,
    pages: pages ?? [],
    notFound,
    mount,
    auth,
    services,
    locales,
    resolvePath,
    onLocaleChange,
    onRoute,
  };
}

function assertRegisteredNavigationPolicies(
  options: ResolvedPageNavigationOptions,
): void {
  const eagerPages = [
    ...collectImmediateNavigationPages(options.pages),
    ...collectImmediateNavigationPages(
      options.notFound ? [options.notFound] : [],
    ),
  ];

  const missingPolicies = findMissingAuthorizationPolicies({
    authorizations: eagerPages.map((page) => resolvePageAuthorization(page)),
    policies: options.auth?.policies,
  });
  if (missingPolicies.length === 0) {
    return;
  }

  throw new Error(
    `Configured pages reference unregistered authorization policies: ${
      missingPolicies.map((policyName) => `"${policyName}"`).join(", ")
    }. Register them under auth.policies before starting navigation.`,
  );
}

function createNavigationSequenceSource(): NavigationSequenceSource {
  let navigationSequence = 0;

  return () => {
    navigationSequence += 1;
    return navigationSequence;
  };
}

function createNavigationSequenceState(
  nextNavigationSequence: NavigationSequenceSource,
): NavigationSequenceState {
  return {
    navigationSequence: nextNavigationSequence(),
    started: false,
    controller: new AbortController(),
  };
}

function throwIfNavigationAborted(sequence: NavigationSequenceState): void {
  if (sequence.controller.signal.aborted) {
    throw new NavigationAbortedError();
  }
}

function emitNavigationStart(
  args: NavigationLifecycleEmissionArgs,
): MainzNavigationStartDetail | undefined {
  if (typeof document === "undefined" || args.sequence.started) {
    return undefined;
  }

  args.sequence.started = true;
  args.sequence.lifecycle = {
    mount: args.mount,
    mode: args.mode,
    navigationType: args.navigationType,
    path: args.path,
    matchedPath: args.matchedPath,
    locale: args.locale,
    url: args.url,
    basePath: args.basePath,
  };

  const detail = {
    mode: args.mode,
    navigationType: args.navigationType,
    path: args.path,
    matchedPath: args.matchedPath,
    locale: args.locale,
    url: args.url.toString(),
    basePath: args.basePath,
    navigationSequence: args.sequence.navigationSequence,
  } satisfies MainzNavigationStartDetail;

  args.mount.dispatchEvent(
    new CustomEvent<MainzNavigationStartDetail>(MAINZ_NAVIGATION_START_EVENT, {
      detail,
      bubbles: true,
    }),
  );

  return detail;
}

function emitNavigationReady(
  args: NavigationLifecycleEmissionArgs,
): MainzNavigationReadyDetail | undefined {
  if (typeof document === "undefined") {
    return undefined;
  }

  if (args.sequence.terminalState) {
    return undefined;
  }

  args.sequence.terminalState = "ready";

  const detail = {
    mode: args.mode,
    navigationType: args.navigationType,
    path: args.path,
    matchedPath: args.matchedPath,
    locale: args.locale,
    url: args.url.toString(),
    basePath: args.basePath,
    navigationSequence: args.sequence.navigationSequence,
  } satisfies MainzNavigationReadyDetail;

  args.mount.dispatchEvent(
    new CustomEvent<MainzNavigationReadyDetail>(MAINZ_NAVIGATION_READY_EVENT, {
      detail,
      bubbles: true,
    }),
  );

  return detail;
}

function finalizeNavigationReady(
  readyArgs: NavigationLifecycleEmissionArgs,
  finalize?: (args: NavigationLifecycleEmissionArgs) => void,
): void {
  if (finalize) {
    finalize(readyArgs);
    return;
  }

  emitNavigationReady(readyArgs);
}

function emitNavigationError(
  args: NavigationLifecycleEmissionArgs & {
    phase: MainzNavigationErrorDetail["phase"];
    error: unknown;
  },
): MainzNavigationErrorDetail | undefined {
  if (typeof document === "undefined") {
    return undefined;
  }

  if (args.sequence.terminalState) {
    return undefined;
  }

  args.sequence.terminalState = "error";

  const detail = {
    mode: args.mode,
    navigationType: args.navigationType,
    path: args.path,
    matchedPath: args.matchedPath,
    locale: args.locale,
    url: args.url.toString(),
    basePath: args.basePath,
    navigationSequence: args.sequence.navigationSequence,
    phase: args.phase,
    message: toErrorMessage(args.error),
    error: args.error,
  } satisfies MainzNavigationErrorDetail;

  args.mount.dispatchEvent(
    new CustomEvent<MainzNavigationErrorDetail>(MAINZ_NAVIGATION_ERROR_EVENT, {
      detail,
      bubbles: true,
    }),
  );

  return detail;
}

function abortNavigationSequence(
  sequence: NavigationSequenceState | undefined,
  args: Partial<NavigationLifecycleBaseArgs> & {
    reason: MainzNavigationAbortDetail["reason"];
  },
): MainzNavigationAbortDetail | undefined {
  if (!sequence || sequence.terminalState) {
    return undefined;
  }

  const lifecycle = sequence.lifecycle;
  const mount = lifecycle?.mount ?? args.mount;
  const mode = lifecycle?.mode ?? args.mode;
  const navigationType = lifecycle?.navigationType ?? args.navigationType;
  const path = lifecycle?.path ?? args.path;
  const matchedPath = lifecycle?.matchedPath ?? args.matchedPath;
  const locale = lifecycle?.locale ?? args.locale;
  const url = lifecycle?.url ?? args.url;
  const basePath = lifecycle?.basePath ?? args.basePath;

  if (
    !mount || !mode || !navigationType || !path || !matchedPath || !url ||
    !basePath
  ) {
    return undefined;
  }

  sequence.terminalState = "abort";
  sequence.controller.abort();

  const detail = {
    mode,
    navigationType,
    path,
    matchedPath,
    locale,
    url: url.toString(),
    basePath,
    navigationSequence: sequence.navigationSequence,
    reason: args.reason,
  } satisfies MainzNavigationAbortDetail;

  mount.dispatchEvent(
    new CustomEvent<MainzNavigationAbortDetail>(MAINZ_NAVIGATION_ABORT_EVENT, {
      detail,
      bubbles: true,
    }),
  );

  return detail;
}

function collectImmediateNavigationPages(
  entries:
    readonly (SpaPageConstructor | SpaPageDefinition | SpaLazyPageDefinition)[],
): SpaPageConstructor[] {
  const pages: SpaPageConstructor[] = [];

  for (const entry of entries) {
    if (isSpaLazyPageDefinition(entry)) {
      continue;
    }

    pages.push(isSpaPageDefinition(entry) ? entry.page : entry);
  }

  return pages;
}

function assertRegisteredPagePolicies(
  page: SpaPageConstructor,
  auth: AuthorizationRuntimeOptions | undefined,
): void {
  const missingPolicies = findMissingAuthorizationPolicies({
    authorizations: [resolvePageAuthorization(page)],
    policies: auth?.policies,
  });
  if (missingPolicies.length === 0) {
    return;
  }

  throw new Error(
    `Page "${page.name}" references unregistered authorization policies: ${
      missingPolicies.map((policyName) => `"${policyName}"`).join(", ")
    }. Register them under auth.policies before rendering protected routes.`,
  );
}

function resolveMainzNavigationMode(): NavigationMode {
  if (typeof __MAINZ_NAVIGATION_MODE__ !== "undefined") {
    return __MAINZ_NAVIGATION_MODE__;
  }

  const fromGlobal =
    (globalThis as Record<string, unknown>).__MAINZ_NAVIGATION_MODE__;
  if (fromGlobal === "spa" || fromGlobal === "mpa") {
    return fromGlobal;
  }

  return "mpa";
}

function resolveMainzBasePath(): string {
  if (typeof __MAINZ_BASE_PATH__ !== "undefined") {
    return __MAINZ_BASE_PATH__;
  }

  const fromGlobal =
    (globalThis as Record<string, unknown>).__MAINZ_BASE_PATH__;
  return typeof fromGlobal === "string" && fromGlobal.trim() ? fromGlobal : "/";
}

function resolveMainzDefaultLocale(): string | undefined {
  if (typeof __MAINZ_DEFAULT_LOCALE__ !== "undefined") {
    return __MAINZ_DEFAULT_LOCALE__;
  }

  const fromGlobal =
    (globalThis as Record<string, unknown>).__MAINZ_DEFAULT_LOCALE__;
  return typeof fromGlobal === "string" && fromGlobal.trim()
    ? fromGlobal
    : undefined;
}

function resolveSpaPageRenderMode(page: SpaPageConstructor): PageRenderMode {
  return resolvePageRenderMode(page as object) ?? "csr";
}

function resolveMainzLocalePrefix(): "except-default" | "always" {
  if (typeof __MAINZ_LOCALE_PREFIX__ !== "undefined") {
    return __MAINZ_LOCALE_PREFIX__;
  }

  const fromGlobal =
    (globalThis as Record<string, unknown>).__MAINZ_LOCALE_PREFIX__;
  return fromGlobal === "always" ? "always" : "except-default";
}

function resolveMainzSiteUrl(): string | undefined {
  if (typeof __MAINZ_SITE_URL__ !== "undefined") {
    return __MAINZ_SITE_URL__ || undefined;
  }

  const fromGlobal = (globalThis as Record<string, unknown>).__MAINZ_SITE_URL__;
  return typeof fromGlobal === "string" && fromGlobal.trim()
    ? fromGlobal
    : undefined;
}

function readMainzAppLocales(): readonly string[] {
  if (typeof __MAINZ_APP_LOCALES__ !== "undefined") {
    return __MAINZ_APP_LOCALES__;
  }

  const appLocalesFromGlobal =
    (globalThis as Record<string, unknown>).__MAINZ_APP_LOCALES__;
  if (Array.isArray(appLocalesFromGlobal)) {
    return appLocalesFromGlobal.filter((value): value is string =>
      typeof value === "string"
    );
  }

  return [];
}

async function bootstrapDocumentNavigation(
  mode: NavigationMode,
  options: ResolvedPageNavigationOptions,
  basePath: string,
  nextNavigationSequence: NavigationSequenceSource,
): Promise<void> {
  if (!options.pages.length && !options.notFound) {
    return;
  }

  const mount = resolveSpaMount(options.mount);
  ensureDefaultAppPortalLayer(mount);
  const routes = normalizeSpaRoutes(options.pages, options.notFound);
  const url = new URL(window.location.href);
  const currentPath =
    resolveRoutePath(url, basePath, options.resolvePath, options.locales) ??
      "/";
  const routeMatch = findMatchingSpaRoute(routes, currentPath);
  const locale = resolveNavigationLocale(url, basePath, options.locales);
  const sequence = createNavigationSequenceState(nextNavigationSequence);

  emitNavigationStart({
    sequence,
    mount,
    mode,
    navigationType: "initial",
    path: routeMatch?.route.path ?? currentPath,
    matchedPath: currentPath,
    locale,
    url,
    basePath,
  });

  try {
    const existingContext = await resolveMountedRouteContext(mount, routes, {
      appId: options.appId,
      assets: options.assets,
      routeMatch,
      url,
      currentPath,
      basePath,
      locale,
      auth: options.auth,
      serviceContainer: options.serviceContainer,
      locales: options.locales,
      mode,
      nextNavigationSequence,
      sequence,
    });
    if (existingContext && typeof existingContext !== "string") {
      applyNavigationLocale(locale, url, basePath, options.onLocaleChange);
      options.onRoute?.(existingContext);
      emitNavigationReady({
        sequence,
        mount,
        mode,
        navigationType: existingContext.navigationType,
        path: existingContext.path,
        matchedPath: existingContext.matchedPath,
        locale: existingContext.locale,
        url: existingContext.url,
        basePath: existingContext.basePath,
      });
      return;
    }

    if (existingContext === "redirected" || existingContext === "forbidden") {
      if (existingContext === "forbidden") {
        applyNavigationLocale(locale, url, basePath, options.onLocaleChange);
        emitNavigationReady({
          sequence,
          mount,
          mode,
          navigationType: "initial",
          path: routeMatch?.route.path ?? currentPath,
          matchedPath: currentPath,
          locale,
          url,
          basePath,
        });
      }
      return;
    }

    await renderSpaRoute({
      appId: options.appId,
      assets: options.assets,
      routes,
      mount,
      url,
      navigationType: "initial",
      basePath,
      auth: options.auth,
      serviceContainer: options.serviceContainer,
      locales: options.locales,
      resolvePath: options.resolvePath,
      onLocaleChange: options.onLocaleChange,
      onRoute: options.onRoute,
      mode,
      nextNavigationSequence,
      sequence,
    });
  } catch (error) {
    emitNavigationError({
      sequence,
      mount,
      mode,
      navigationType: "initial",
      path: routeMatch?.route.path ?? currentPath,
      matchedPath: currentPath,
      locale,
      url,
      basePath,
      phase: "document-bootstrap",
      error,
    });
    throw error;
  }
}

async function resolveMountedRouteContext(
  mount: HTMLElement,
  routes: readonly NormalizedSpaRoute[],
  context: {
    appId?: string;
    assets?: readonly AssetDefinition[];
    routeMatch?: SpaRouteMatch;
    url: URL;
    currentPath: string;
    basePath: string;
    locale?: string;
    auth?: AuthorizationRuntimeOptions;
    serviceContainer?: ServiceContainer;
    locales?: readonly string[];
    mode: NavigationMode;
    nextNavigationSequence: NavigationSequenceSource;
    sequence: NavigationSequenceState;
  },
): Promise<SpaNavigationRenderContext | "redirected" | "forbidden" | null> {
  const routeSnapshot = readInitialRouteSnapshot();
  const matchedPage = context.routeMatch?.route.page;
  if (matchedPage) {
    ensurePageCustomElement(matchedPage);

    const mountedElement = mount.querySelector(matchedPage.getTagName());
    if (isHtmlElement(mountedElement)) {
      const params = context.routeMatch?.params ?? {};
      const authorization = resolvePageAuthorization(matchedPage);
      const principal = await resolveCurrentPrincipal(context.auth);
      const accessDecision = await evaluatePageAuthorization({
        authorization,
        principal,
        policies: context.auth?.policies,
      });

      if (accessDecision.status === "redirect-login") {
        await redirectUnauthorizedRouteToLogin({
          routes,
          mount,
          url: context.url,
          navigationType: "initial",
          basePath: context.basePath,
          currentPath: context.currentPath,
          locale: context.locale,
          auth: context.auth,
          serviceContainer: context.serviceContainer,
          locales: context.locales,
          mode: context.mode,
          nextNavigationSequence: context.nextNavigationSequence,
          sequence: context.sequence,
        });
        return "redirected";
      }

      if (accessDecision.status === "forbidden") {
        renderForbiddenRoute(mount);
        return "forbidden";
      }

      const matchedSnapshot = resolveMatchingSnapshot(routeSnapshot, {
        mountedElement,
        path: context.routeMatch?.route.path ?? context.currentPath,
        matchedPath: context.currentPath,
        params,
        locale: context.locale,
      });
      if (matchedSnapshot) {
        const snapshotRouteContext = attachServiceContainer(
          createRouteContext({
            path: context.routeMatch?.route.path ?? context.currentPath,
            matchedPath: context.currentPath,
            params,
            locale: context.locale,
            url: context.url,
            renderMode: resolveSpaPageRenderMode(matchedPage),
            navigationMode: context.mode,
            principal,
            profile: createRouteProfileContext(context.basePath),
          }),
          context.serviceContainer,
        );
        (mountedElement as RoutedPageElement).data = matchedSnapshot.data;
        applyPageLifecycleProps(mountedElement as RoutedPageElement, {
          route: snapshotRouteContext,
          data: matchedSnapshot.data,
          metadata: matchedSnapshot.metadata,
          assets: matchedSnapshot.assets,
        });
      }
      const data = matchedSnapshot
        ? matchedSnapshot.data
        : await resolvePageRouteData({
          page: matchedPage,
          pageElement: mountedElement as RoutedPageElement,
          path: context.routeMatch?.route.path ?? context.currentPath,
          matchedPath: context.currentPath,
          params,
          locale: context.locale,
          principal,
          url: context.url,
          signal: context.sequence.controller.signal,
          basePath: context.basePath,
          navigationMode: context.mode,
          serviceContainer: context.serviceContainer,
        });
      const metadata = matchedSnapshot?.metadata ?? resolveSpaRouteMetadata({
        page: matchedPage,
        pageElement: mountedElement as RoutedPageElement,
        path: context.routeMatch?.route.path ?? context.currentPath,
        matchedPath: context.currentPath,
        locale: context.locale,
        locales: context.locales,
        data,
        principal,
        url: context.url,
        basePath: context.basePath,
        navigationMode: context.mode,
      });
      const assets = matchedSnapshot?.assets ?? resolveSpaRouteAssets({
        appId: context.appId,
        appAssets: context.assets,
        page: matchedPage,
        pageElement: mountedElement as RoutedPageElement,
        path: context.routeMatch?.route.path ?? context.currentPath,
        matchedPath: context.currentPath,
        locale: context.locale,
        principal,
        url: context.url,
        basePath: context.basePath,
        navigationMode: context.mode,
      });
      const routeContext = {
        page: matchedPage,
        path: context.routeMatch?.route.path ?? context.currentPath,
        matchedPath: context.currentPath,
        params: context.routeMatch?.params ?? {},
        principal,
        authorization,
        data,
        metadata,
        assets,
        locale: context.locale,
        url: context.url,
        navigationType: "initial",
        basePath: context.basePath,
      } satisfies SpaNavigationRenderContext;
      attachServiceContainer(routeContext, context.serviceContainer);

      applySpaRouteContext(mountedElement, routeContext);
      applyResolvedPageMetadataToDocument(routeContext.metadata);
      applyResolvedAssetDefinitionsToDocument(routeContext.assets);
      return routeContext;
    }
  }

  for (const route of routes) {
    if (!route.page) {
      continue;
    }

    const tagName = route.page.getTagName();
    const mountedElement = mount.querySelector(tagName);
    if (!isHtmlElement(mountedElement)) {
      continue;
    }

    ensurePageCustomElement(route.page);

    const params = context.routeMatch?.route === route
      ? context.routeMatch.params
      : {};
    const authorization = resolvePageAuthorization(route.page);
    const principal = await resolveCurrentPrincipal(context.auth);
    const accessDecision = await evaluatePageAuthorization({
      authorization,
      principal,
      policies: context.auth?.policies,
    });

    if (accessDecision.status === "redirect-login") {
      await redirectUnauthorizedRouteToLogin({
        routes,
        mount,
        url: context.url,
        navigationType: "initial",
        basePath: context.basePath,
        currentPath: context.currentPath,
        locale: context.locale,
        auth: context.auth,
        locales: context.locales,
        mode: context.mode,
        nextNavigationSequence: context.nextNavigationSequence,
        sequence: context.sequence,
      });
      return "redirected";
    }

    if (accessDecision.status === "forbidden") {
      renderForbiddenRoute(mount);
      return "forbidden";
    }

    const matchedSnapshot = resolveMatchingSnapshot(routeSnapshot, {
      mountedElement,
      path: route.path,
      matchedPath: context.currentPath,
      params,
      locale: context.locale,
    });
    if (matchedSnapshot) {
      const snapshotRouteContext = attachServiceContainer(
        createRouteContext({
          path: route.path,
          matchedPath: context.currentPath,
          params,
          locale: context.locale,
          url: context.url,
          renderMode: resolveSpaPageRenderMode(route.page),
          navigationMode: context.mode,
          principal,
          profile: createRouteProfileContext(context.basePath),
        }),
        context.serviceContainer,
      );
      (mountedElement as RoutedPageElement).data = matchedSnapshot.data;
      applyPageLifecycleProps(mountedElement as RoutedPageElement, {
        route: snapshotRouteContext,
        data: matchedSnapshot.data,
        metadata: matchedSnapshot.metadata,
        assets: matchedSnapshot.assets,
      });
    }
    const data = matchedSnapshot
      ? matchedSnapshot.data
      : await resolvePageRouteData({
        page: route.page,
        pageElement: mountedElement as RoutedPageElement,
        path: route.path,
        matchedPath: context.currentPath,
        params,
        locale: context.locale,
        principal,
        url: context.url,
        signal: context.sequence.controller.signal,
        basePath: context.basePath,
        navigationMode: context.mode,
        serviceContainer: context.serviceContainer,
      });
    const metadata = matchedSnapshot?.metadata ?? resolveSpaRouteMetadata({
      page: route.page,
      pageElement: mountedElement as RoutedPageElement,
      path: route.path,
      matchedPath: context.currentPath,
      locale: context.locale,
      locales: context.locales,
      data,
      principal,
      url: context.url,
      basePath: context.basePath,
      navigationMode: context.mode,
    });
    const assets = matchedSnapshot?.assets ?? resolveSpaRouteAssets({
      appId: context.appId,
      appAssets: context.assets,
      page: route.page,
      pageElement: mountedElement as RoutedPageElement,
      path: route.path,
      matchedPath: context.currentPath,
      locale: context.locale,
      principal,
      url: context.url,
      basePath: context.basePath,
      navigationMode: context.mode,
    });
    const routeContext = {
      page: route.page,
      path: route.path,
      matchedPath: context.currentPath,
      params,
      principal,
      authorization,
      data,
      metadata,
      assets,
      locale: context.locale,
      url: context.url,
      navigationType: "initial",
      basePath: context.basePath,
    } satisfies SpaNavigationRenderContext;
    attachServiceContainer(routeContext, context.serviceContainer);

    applySpaRouteContext(mountedElement, routeContext);
    applyResolvedPageMetadataToDocument(routeContext.metadata);
    applyResolvedAssetDefinitionsToDocument(routeContext.assets);
    return routeContext;
  }

  return null;
}

function readInitialRouteSnapshot(): InitialRouteSnapshot | undefined {
  const snapshotScript = document.getElementById("mainz-route-snapshot");
  if (
    !(snapshotScript instanceof Element) || snapshotScript.tagName !== "SCRIPT"
  ) {
    return undefined;
  }

  const snapshotText = snapshotScript.textContent?.trim();
  if (!snapshotText) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(snapshotText) as Record<string, unknown>;
    if (!isInitialRouteSnapshot(parsed)) {
      return undefined;
    }

    return parsed;
  } catch {
    return undefined;
  }
}

function resolveMatchingSnapshot(
  snapshot: InitialRouteSnapshot | undefined,
  args: {
    mountedElement: HTMLElement;
    path: string;
    matchedPath: string;
    params: Record<string, string>;
    locale?: string;
  },
): InitialRouteSnapshot | undefined {
  if (!snapshot) {
    return undefined;
  }

  if (snapshot.pageTagName !== args.mountedElement.tagName.toLowerCase()) {
    return undefined;
  }

  if (
    snapshot.path !== args.path || snapshot.matchedPath !== args.matchedPath
  ) {
    return undefined;
  }

  if ((snapshot.locale ?? undefined) !== (args.locale ?? undefined)) {
    return undefined;
  }

  if (!recordsEqual(snapshot.params, args.params)) {
    return undefined;
  }

  return snapshot;
}

function isInitialRouteSnapshot(value: unknown): value is InitialRouteSnapshot {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return typeof candidate.pageTagName === "string" &&
    typeof candidate.path === "string" &&
    typeof candidate.matchedPath === "string" &&
    isStringRecord(candidate.params) &&
    (typeof candidate.locale === "string" ||
      typeof candidate.locale === "undefined") &&
    (typeof candidate.assets === "undefined" ||
      isAssetDefinitionList(candidate.assets)) &&
    (typeof candidate.metadata === "undefined" ||
      isPageMetadataDefinition(candidate.metadata));
}

function recordsEqual(
  left: Record<string, string>,
  right: Record<string, string>,
): boolean {
  const leftEntries = Object.entries(left);
  const rightEntries = Object.entries(right);
  if (leftEntries.length !== rightEntries.length) {
    return false;
  }

  return leftEntries.every(([key, value]) => right[key] === value);
}

function isStringRecord(value: unknown): value is Record<string, string> {
  return typeof value === "object" && value !== null &&
    Object.values(value).every((entry) => typeof entry === "string");
}

function isPageMetadataDefinition(
  value: unknown,
): value is PageMetadataDefinition {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return "title" in candidate || "meta" in candidate || "links" in candidate;
}
